import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  BookOpen, BarChart3, ClipboardCheck, ChevronDown,
  Save, CheckCircle2, AlertCircle, Loader2, Info,
  Settings, Plus, Trash2, Edit2, ChevronRight, ChevronDown as ChevDown,
  Check, X, GripVertical, Upload, Download, FileText,
  Users, TrendingUp, TrendingDown, UserCheck, UserX,
  CalendarCheck, ShieldAlert, GraduationCap, Star,
  Trophy, AlertTriangle, ClipboardList, Clock, Activity,
  Maximize2, Minimize2, Search, ArrowLeft,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  LabelList, ResponsiveContainer, PieChart, Pie, Cell,
  LineChart, Line, AreaChart, Area, ComposedChart, ReferenceLine,
  ScatterChart, Scatter, ZAxis,
} from 'recharts';
import { trackerAPI, schoolConfigAPI, rbacAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { YearContext } from '../components/Layout/Layout';
import { PageLoader } from '../components/Common/LoadingSpinner';
import EmailNotificationSetup    from '../components/Common/EmailNotificationSetup';
import TelegramNotificationSetup from '../components/Common/TelegramNotificationSetup';
import toast from 'react-hot-toast';

// ── Grade scale — mirrors Teacher Observation Rating (5 → 1) ───────
// 5=E Exceeding, 4=P Proficient, 3=M Meeting, 2=D Developing, 1=W Working
const GRADES = ['E','P','M','D','W'];          // highest → lowest (display order)
const GRADE_CFG = {
  E: { label:'Exceeding',  range:'4.1 – 5.0', color:'#6ee7b7', bg:'bg-emerald-50', text:'text-emerald-700', border:'border-emerald-200', circle:'bg-emerald-200 text-emerald-800' },
  P: { label:'Proficient', range:'3.1 – 4.0', color:'#93c5fd', bg:'bg-blue-50',    text:'text-blue-700',    border:'border-blue-200',    circle:'bg-blue-200 text-blue-800'       },
  M: { label:'Meeting',    range:'2.1 – 3.0', color:'#d8b4fe', bg:'bg-purple-50',  text:'text-purple-700',  border:'border-purple-200',  circle:'bg-purple-200 text-purple-800'   },
  D: { label:'Developing', range:'1.1 – 2.0', color:'#fcd34d', bg:'bg-amber-50',   text:'text-amber-700',   border:'border-amber-200',   circle:'bg-amber-200 text-amber-800'     },
  W: { label:'Working',    range:'0 – 1.0',   color:'#fca5a5', bg:'bg-red-50',     text:'text-red-700',     border:'border-red-200',     circle:'bg-red-200 text-red-800'         },
};
// Score (1–5) ↔ Grade letter conversions
const GRADE_TO_SCORE = { E:5, P:4, M:3, D:2, W:1 };
const SCORE_TO_GRADE = { 5:'E', 4:'P', 3:'M', 2:'D', 1:'W' };

function GradeBadge({ grade }) {
  if (!grade || !GRADE_CFG[grade]) return <span className="text-slate-300 text-xs">—</span>;
  const cfg = GRADE_CFG[grade];
  return (
    <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold border ${cfg.bg} ${cfg.text} ${cfg.border}`}>
      {grade}
    </span>
  );
}

// ── Grade rating cell — 5 circles (5→1), same design as Teacher Obs ──
// Click a circle to set grade; click again to clear. 5=E … 1=W.
function GradeCell({ value, onChange, disabled }) {
  const activeScore = value ? GRADE_TO_SCORE[value] : null;
  return (
    <div className="flex gap-1 justify-center">
      {[5, 4, 3, 2, 1].map(n => {
        const g   = SCORE_TO_GRADE[n];
        const cfg = GRADE_CFG[g];
        const on  = activeScore === n;
        return (
          <button
            key={n}
            type="button"
            disabled={disabled}
            onClick={() => onChange(on ? null : g)}
            title={`${n} – ${cfg.label} (${cfg.range})`}
            className={`w-7 h-7 rounded-full text-[11px] font-bold border-2 transition-all select-none ${
              on
                ? `${cfg.circle} shadow scale-110 border-transparent`
                : 'bg-white text-slate-400 border-slate-200 hover:border-slate-400 hover:text-slate-600'
            } ${disabled ? 'cursor-not-allowed opacity-40' : 'cursor-pointer'}`}
          >
            {n}
          </button>
        );
      })}
    </div>
  );
}

// ── Recharts tooltip ──────────────────────────────────────
function ChartTip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-lg px-3 py-2 text-xs">
      <p className="font-semibold text-slate-700 mb-1">{label}</p>
      {payload.map(p => (
        <div key={p.dataKey} className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full" style={{ background: p.fill }} />
          <span className="text-slate-600">{p.name}:</span>
          <span className="font-bold">{p.value}</span>
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════
// ── Canonical grade list ───────────────────────────────────
const ALL_GRADES = ['Nursery','KG 1','KG 2','I','II','III','IV','V','VI','VI-ICSE','VII','VII-ICSE','VIII','VIII-ICSE'];

// Roles that see school-wide analytics; everyone else gets a class-scoped view
const SCHOOL_VIEW_ROLES = new Set(['admin','super_admin','principal','vice_principal','management']);

// ══════════════════════════════════════════════════════════
// TRACKER SETUP — super admin only
// ══════════════════════════════════════════════════════════
function TrackerSetup() {
  const [selGrade,    setSelGrade]    = useState('IV');
  const [subjects,    setSubjects]    = useState([]);
  const [selSubject,  setSelSubject]  = useState(null);
  const [config,      setConfig]      = useState(null);
  const [loadingSubj, setLoadingSubj] = useState(false);
  const [loadingCfg,  setLoadingCfg]  = useState(false);
  const [activePanel, setActivePanel] = useState('strands');

  // Copy to grade state — multi-select
  // copyTargets: { grade: { checked, subjects: [], selectedSubjectId, loading } }
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copyTargets,   setCopyTargets]   = useState({});
  const [copying,         setCopying]       = useState(false);
  const [copyResults,     setCopyResults]   = useState([]);
  const [schoolImporting, setSchoolImporting] = useState(false);
  const schoolImportRef = useRef(null);
  const [importing,     setImporting]     = useState(false);
  const importRef = useRef(null);

  // Inline editing state
  const [newStrandName,   setNewStrandName]   = useState('');
  const [editStrand,      setEditStrand]      = useState(null);
  const [expandedStrand,  setExpandedStrand]  = useState(null);
  const [newSubName,      setNewSubName]      = useState('');
  const [editSub,         setEditSub]         = useState(null); // { id, name }
  const [newWeek,         setNewWeek]         = useState({ name:'', start_date:'', end_date:'' });
  const [editWeek,        setEditWeek]        = useState(null);
  const [saving,          setSaving]          = useState(false);
  const weekImportRef = useRef(null);

  // Load subjects for selected grade
  const loadSubjects = useCallback(async () => {
    if (!selGrade) return;
    setLoadingSubj(true);
    try {
      const r = await trackerAPI.getSetupSubjects({ grade: selGrade });
      setSubjects(r.data);
    } catch { toast.error('Failed to load subjects'); }
    finally { setLoadingSubj(false); }
  }, [selGrade]);

  useEffect(() => { loadSubjects(); }, [loadSubjects]);

  // Load config for selected subject
  const loadConfig = useCallback(async () => {
    if (!selSubject?.tracker_id) { setConfig(null); return; }
    setLoadingCfg(true);
    try {
      const r = await trackerAPI.getSubjectConfig(selSubject.tracker_id);
      setConfig(r.data);
    } catch { toast.error('Failed to load config'); }
    finally { setLoadingCfg(false); }
  }, [selSubject]);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  const enableSubject = async (subj) => {
    try {
      const r = await trackerAPI.createSubject({ subjectId: subj.subject_id });
      toast.success(r.data.message);
      await loadSubjects();
      // Re-select with new tracker_id
      const updated = { ...subj, tracker_id: r.data.id, is_configured: true };
      setSelSubject(updated);
    } catch (e) {
      // Already configured → 409
      if (e.response?.status === 409) {
        const existing = { ...subj, tracker_id: e.response.data.id, is_configured: true };
        setSelSubject(existing);
      } else {
        toast.error(e.response?.data?.error || 'Failed to enable subject');
      }
    }
  };

  const disableSubject = async (trackerId) => {
    if (!window.confirm('Remove this subject from the Learning Tracker? Strand config is kept.')) return;
    try {
      await trackerAPI.updateSubject(trackerId, { isActive: false });
      toast.success('Subject removed from tracker');
      setSelSubject(null); setConfig(null);
      loadSubjects();
    } catch (e) { toast.error(e.response?.data?.error || 'Failed'); }
  };

  // ── Strands ──────────────────────────────────────────
  const addStrand = async () => {
    if (!newStrandName.trim()) return;
    const current = config?.strands || [];
    const updated = [...current, { name: newStrandName.trim() }];
    setSaving(true);
    try {
      await trackerAPI.saveStrands(selSubject.tracker_id, { strands: updated });
      setNewStrandName(''); loadConfig();
    } catch { toast.error('Failed to save strand'); }
    finally { setSaving(false); }
  };

  const updateStrand = async (strand) => {
    const updated = (config?.strands || []).map(s => s.id === strand.id ? { ...s, name: editStrand.name } : s);
    setSaving(true);
    try {
      await trackerAPI.saveStrands(selSubject.tracker_id, { strands: updated });
      setEditStrand(null); loadConfig();
    } catch { toast.error('Failed to update strand'); }
    finally { setSaving(false); }
  };

  const deleteStrand = async (strandId) => {
    if (!window.confirm('Delete this strand and all its sub-strands?')) return;
    const updated = (config?.strands || []).filter(s => s.id !== strandId);
    try {
      await trackerAPI.saveStrands(selSubject.tracker_id, { strands: updated });
      loadConfig();
    } catch { toast.error('Failed to delete strand'); }
  };

  // ── Sub-strands ───────────────────────────────────────
  const addSubstrand = async (strandId) => {
    if (!newSubName.trim()) return;
    const current = (config?.substrands || []).filter(ss => ss.strand_id === strandId);
    const updated = [...current, { name: newSubName.trim() }];
    setSaving(true);
    try {
      await trackerAPI.saveSubstrands(strandId, { substrands: updated });
      setNewSubName(''); loadConfig();
    } catch { toast.error('Failed to save sub-strand'); }
    finally { setSaving(false); }
  };

  const updateSubstrand = async (substrand) => {
    const strandId = substrand.strand_id;
    const current  = (config?.substrands || []).filter(ss => ss.strand_id === strandId);
    const updated  = current.map(ss => ss.id === substrand.id ? { ...ss, name: editSub.name } : ss);
    setSaving(true);
    try {
      await trackerAPI.saveSubstrands(strandId, { substrands: updated });
      setEditSub(null); loadConfig();
    } catch { toast.error('Failed to update sub-strand'); }
    finally { setSaving(false); }
  };

  const deleteSubstrand = async (substrand) => {
    const strandId = substrand.strand_id;
    const current  = (config?.substrands || []).filter(ss => ss.strand_id === strandId);
    const updated  = current.filter(ss => ss.id !== substrand.id);
    try {
      await trackerAPI.saveSubstrands(strandId, { substrands: updated });
      loadConfig();
    } catch { toast.error('Failed to delete sub-strand'); }
  };

  // ── Weeks ─────────────────────────────────────────────
  const fmtShortDate = (dateStr) => {
    if (!dateStr) return '';
    const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const d = new Date(dateStr + 'T12:00:00');
    return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
  };
  const buildDateRange = (start, end) => {
    if (!start && !end) return '';
    if (!end) return fmtShortDate(start);
    return `${fmtShortDate(start)} – ${fmtShortDate(end)}`;
  };

  const addWeek = async () => {
    if (!newWeek.name.trim()) { toast.error('Week name required'); return; }
    const current  = config?.weeks || [];
    const nextNum  = Math.max(0, ...current.map(w => w.week_number || 0)) + 1;
    const updated  = [...current, {
      week_number: nextNum,
      name: newWeek.name.trim(),
      start_date: newWeek.start_date || null,
      end_date:   newWeek.end_date   || null,
      date_range: buildDateRange(newWeek.start_date, newWeek.end_date),
    }];
    setSaving(true);
    try {
      await trackerAPI.saveWeeks(selSubject.tracker_id, { weeks: updated });
      setNewWeek({ name:'', start_date:'', end_date:'' }); loadConfig();
    } catch { toast.error('Failed to save week'); }
    finally { setSaving(false); }
  };

  const updateWeek = async () => {
    const updated = (config?.weeks || []).map(w => w.id === editWeek.id ? {
      ...editWeek,
      date_range: buildDateRange(editWeek.start_date, editWeek.end_date),
    } : w);
    setSaving(true);
    try {
      await trackerAPI.saveWeeks(selSubject.tracker_id, { weeks: updated });
      setEditWeek(null); loadConfig();
    } catch { toast.error('Failed to update week'); }
    finally { setSaving(false); }
  };

  const deleteWeek = async (weekId) => {
    if (!window.confirm('Delete this week?')) return;
    const updated = (config?.weeks || []).filter(w => w.id !== weekId);
    try {
      await trackerAPI.saveWeeks(selSubject.tracker_id, { weeks: updated });
      loadConfig();
    } catch { toast.error('Failed to delete week'); }
  };

  const downloadWeekTemplate = async () => {
    try {
      const r = await trackerAPI.weekTemplate(selSubject.tracker_id);
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'week_template.xlsx'; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download template'); }
  };

  const exportWeeks = async () => {
    try {
      const r = await trackerAPI.exportWeeks(selSubject.tracker_id);
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'weeks_export.xlsx'; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Failed to export weeks'); }
  };

  const importWeeksFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try {
      const r = await trackerAPI.importWeeks(selSubject.tracker_id, fd);
      toast.success(`Imported ${r.data.imported} week(s)`);
      loadConfig();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Import failed');
    } finally { e.target.value = ''; }
  };

  // ── School-level template / export / import ──────────
  const handleSchoolTemplate = async () => {
    try {
      const r = await trackerAPI.getSchoolTemplate();
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a'); a.href=url; a.download='tracker_school_template.xlsx'; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Template download failed'); }
  };

  const handleSchoolExport = async () => {
    try {
      const r = await trackerAPI.exportSchoolConfig();
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a'); a.href=url; a.download='tracker_school_config.xlsx'; a.click();
      URL.revokeObjectURL(url);
      toast.success('School configuration exported');
    } catch { toast.error('Export failed'); }
  };

  const handleSchoolImport = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setSchoolImporting(true);
    const fd = new FormData(); fd.append('file', file);
    try {
      const r = await trackerAPI.importSchoolConfig(fd);
      toast.success(r.data.message, { duration:6000 });
      if (r.data.errors?.length) toast.error(`${r.data.errors.length} error(s)`, { duration:5000 });
      loadSubjects();
    } catch (e) { toast.error(e.response?.data?.error || 'Import failed'); }
    finally { setSchoolImporting(false); e.target.value=''; }
  };

  // ── Copy to grades (multi-select) ────────────────────
  const openCopyModal = () => {
    // Pre-build entries for every grade except current
    const targets = {};
    ALL_GRADES.filter(g => g !== selGrade).forEach(g => {
      targets[g] = { checked: false, subjects: [], selectedSubjectId: '', loading: false };
    });
    setCopyTargets(targets);
    setCopyResults([]);
    setShowCopyModal(true);
  };

  const toggleGradeCheck = async (grade) => {
    const current = copyTargets[grade];
    const nextChecked = !current.checked;

    // If checking, load subjects for that grade
    if (nextChecked && current.subjects.length === 0) {
      setCopyTargets(prev => ({ ...prev, [grade]: { ...prev[grade], checked: true, loading: true } }));
      try {
        const r = await trackerAPI.getSetupSubjects({ grade });
        const subs = r.data.filter(s => s.subject_id !== selSubject?.subject_id);
        // Auto-select subject with the same name as source
        const autoMatch = subs.find(s => s.name.toLowerCase() === selSubject?.name.toLowerCase());
        setCopyTargets(prev => ({
          ...prev,
          [grade]: {
            ...prev[grade],
            loading: false,
            subjects: subs,
            selectedSubjectId: autoMatch ? String(autoMatch.subject_id) : '',
          },
        }));
      } catch {
        setCopyTargets(prev => ({ ...prev, [grade]: { ...prev[grade], checked: false, loading: false } }));
        toast.error(`Failed to load subjects for Grade ${grade}`);
      }
    } else {
      setCopyTargets(prev => ({ ...prev, [grade]: { ...prev[grade], checked: nextChecked } }));
    }
  };

  const setGradeSubject = (grade, subjectId) => {
    setCopyTargets(prev => ({ ...prev, [grade]: { ...prev[grade], selectedSubjectId: subjectId } }));
  };

  const handleCopyAll = async () => {
    const targets = Object.entries(copyTargets)
      .filter(([, v]) => v.checked && v.selectedSubjectId)
      .map(([grade, v]) => ({ grade, subjectId: parseInt(v.selectedSubjectId) }));

    if (!targets.length) { toast.error('Select at least one grade and subject'); return; }

    setCopying(true);
    setCopyResults([]);
    const results = [];

    for (const t of targets) {
      try {
        const r = await trackerAPI.copyConfig(selSubject.tracker_id, { targetSubjectId: t.subjectId });
        results.push({ grade: t.grade, status: 'ok', message: r.data.message });
      } catch (e) {
        results.push({ grade: t.grade, status: 'error', message: e.response?.data?.error || 'Failed' });
      }
    }

    setCopyResults(results);
    setCopying(false);
    const ok = results.filter(r => r.status === 'ok').length;
    if (ok > 0) toast.success(`Copied to ${ok} grade${ok > 1 ? 's' : ''} successfully`);
  };

  // ── Export ─────────────────────────────────────────────
  const handleExport = async () => {
    try {
      const r = await trackerAPI.exportConfig(selSubject.tracker_id);
      const url  = URL.createObjectURL(new Blob([r.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `tracker_${selSubject.name.replace(/\s+/g,'_')}_config.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success('Config exported');
    } catch { toast.error('Export failed'); }
  };

  // ── Import ─────────────────────────────────────────────
  const handleImportFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setImporting(true);
    const fd = new FormData(); fd.append('file', file);
    try {
      const r = await trackerAPI.importConfig(selSubject.tracker_id, fd);
      toast.success(r.data.message);
      loadConfig();
    } catch (e) { toast.error(e.response?.data?.error || 'Import failed'); }
    finally { setImporting(false); e.target.value=''; }
  };

  const strands    = config?.strands    || [];
  const substrands = config?.substrands || [];
  const weeks      = config?.weeks      || [];

  return (
    <div className="space-y-4">
      {/* Toolbar — school-level Template / Export / Import + Grade filter */}
      <div className="glass-card p-4 flex flex-wrap items-center gap-3">
        <Settings size={16} className="text-primary-600 flex-shrink-0" />
        <span className="text-sm font-semibold text-slate-700">Configure Tracker</span>

        {/* School-level file actions */}
        <div className="flex items-center gap-2 flex-wrap ml-2">
          {/* Template */}
          <button onClick={handleSchoolTemplate}
            className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 rounded-lg transition-colors shadow-sm">
            📋 Template
          </button>
          {/* Export */}
          <button onClick={handleSchoolExport}
            className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 rounded-lg transition-colors shadow-sm">
            📤 Export All
          </button>
          {/* Import */}
          <label className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 border border-primary-200 bg-primary-50 hover:bg-primary-100 text-primary-700 rounded-lg cursor-pointer transition-colors shadow-sm ${schoolImporting?'opacity-60':''}`}>
            {schoolImporting ? <Loader2 size={12} className="animate-spin"/> : '📥'}
            {schoolImporting ? 'Importing…' : 'Import All'}
            <input ref={schoolImportRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleSchoolImport}/>
          </label>
          <span className="text-[10px] text-slate-400 hidden sm:block">All grades &amp; subjects in one file</span>
        </div>

        {/* Grade filter */}
        <div className="flex items-center gap-2 ml-auto">
          <span className="text-xs text-slate-500">Grade:</span>
          <select className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-primary-400"
            value={selGrade} onChange={e => { setSelGrade(e.target.value); setSelSubject(null); setConfig(null); }}>
            <option value="">All</option>
            {ALL_GRADES.map(g => <option key={g} value={g}>{g}</option>)}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* ── Left: Subject list ── */}
        <div className="glass-card overflow-hidden lg:col-span-1">
          <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/60">
            <p className="text-xs font-bold text-slate-600 uppercase tracking-wide">
              Subjects {selGrade ? `— Grade ${selGrade}` : ''}
            </p>
          </div>

          {loadingSubj ? (
            <div className="flex items-center justify-center py-8"><Loader2 size={20} className="animate-spin text-primary-400"/></div>
          ) : subjects.length === 0 ? (
            <div className="px-4 py-8 text-center">
              <p className="text-sm text-slate-400">No subjects found for this grade.</p>
              <p className="text-xs text-slate-300 mt-1">Add grades to subjects in School Config → Subjects.</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-50">
              {subjects.map(s => (
                <button key={s.subject_id}
                  onClick={() => setSelSubject(s)}
                  className={`w-full text-left px-4 py-3 transition-colors flex items-center gap-3 ${
                    selSubject?.subject_id === s.subject_id ? 'bg-primary-50 border-r-2 border-primary-500' : 'hover:bg-slate-50'
                  }`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-700 truncate">{s.name}</p>
                    {s.is_configured ? (
                      <p className="text-[10px] text-slate-400 mt-0.5">
                        {s.strand_count} strand{s.strand_count!==1?'s':''} · {s.week_count} week{s.week_count!==1?'s':''}
                      </p>
                    ) : (
                      <p className="text-[10px] text-amber-500 mt-0.5">Not configured</p>
                    )}
                  </div>
                  {s.is_configured
                    ? <span className="w-2 h-2 rounded-full bg-emerald-400 flex-shrink-0"/>
                    : <span className="w-2 h-2 rounded-full bg-amber-300 flex-shrink-0"/>}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* ── Right: Config panel ── */}
        <div className="lg:col-span-2">
          {!selSubject ? (
            <div className="glass-card p-12 text-center h-full flex flex-col items-center justify-center">
              <Settings size={36} className="text-slate-300 mb-3"/>
              <p className="text-slate-400 font-medium">Select a subject to configure strands and weeks</p>
            </div>
          ) : (
            <div className="glass-card overflow-hidden">
              {/* Subject header */}
              <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/60">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-bold text-slate-700">{selSubject.name}</h3>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {selSubject.is_configured
                        ? `Configured · ${strands.length} strand${strands.length!==1?'s':''} · ${weeks.length} week${weeks.length!==1?'s':''}`
                        : 'Not yet enabled for Learning Tracker'}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0 flex-wrap justify-end">
                    {selSubject.is_configured && (
                      <>
                        {/* Copy to grade */}
                        <button onClick={openCopyModal}
                          className="flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 border border-primary-200 bg-primary-50 hover:bg-primary-100 text-primary-700 rounded-lg transition-colors">
                          Copy to Grade
                        </button>
                        {/* Disable */}
                        <button onClick={() => disableSubject(selSubject.tracker_id)}
                          className="text-xs text-red-500 hover:text-red-700 border border-red-200 px-2.5 py-1.5 rounded-lg hover:bg-red-50 transition-colors">
                          Disable
                        </button>
                      </>
                    )}
                    {!selSubject.is_configured && (
                      <button onClick={() => enableSubject(selSubject)}
                        className="flex items-center gap-1.5 text-xs font-semibold bg-primary-600 text-white px-3 py-1.5 rounded-lg hover:bg-primary-700 transition-colors">
                        <Plus size={13}/> Enable for Tracker
                      </button>
                    )}
                  </div>
                </div>

                {/* Copy-to-grade modal — multi-select */}
                {showCopyModal && (
                  <div className="mt-3 p-4 bg-white border border-primary-200 rounded-xl space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold text-slate-700">
                        Copy strands, sub-strands &amp; weeks to multiple grades:
                      </p>
                      <div className="flex items-center gap-2">
                        {/* Select All / Deselect All */}
                        {(() => {
                          const allGrades = ALL_GRADES.filter(g => g !== selGrade);
                          const checkedCount = allGrades.filter(g => copyTargets[g]?.checked).length;
                          const allChecked = checkedCount === allGrades.length;
                          const handleToggleAll = async () => {
                            if (allChecked) {
                              // Deselect all
                              setCopyTargets(prev => {
                                const next = { ...prev };
                                allGrades.forEach(g => { next[g] = { ...next[g], checked: false }; });
                                return next;
                              });
                            } else {
                              // Select all — load subjects for unchecked grades
                              for (const grade of allGrades) {
                                if (!copyTargets[grade]?.checked) {
                                  // Load inline (simplified — fire and forget per grade)
                                  setCopyTargets(prev => ({ ...prev, [grade]: { ...(prev[grade]||{}), checked: true, loading: true, subjects: prev[grade]?.subjects||[], selectedSubjectId: prev[grade]?.selectedSubjectId||'' } }));
                                  trackerAPI.getSetupSubjects({ grade }).then(r => {
                                    const subs = r.data.filter(s => s.subject_id !== selSubject?.subject_id);
                                    const autoMatch = subs.find(s => s.name.toLowerCase() === selSubject?.name.toLowerCase());
                                    setCopyTargets(prev => ({
                                      ...prev,
                                      [grade]: { ...prev[grade], loading: false, subjects: subs, selectedSubjectId: autoMatch ? String(autoMatch.subject_id) : (prev[grade]?.selectedSubjectId||'') },
                                    }));
                                  }).catch(() => {
                                    setCopyTargets(prev => ({ ...prev, [grade]: { ...prev[grade], loading: false } }));
                                  });
                                }
                              }
                            }
                          };
                          return (
                            <button onClick={handleToggleAll}
                              className="text-xs font-medium text-primary-600 hover:text-primary-800 underline">
                              {allChecked ? 'Deselect All' : 'Select All'}
                            </button>
                          );
                        })()}
                        <button onClick={()=>{setShowCopyModal(false);setCopyResults([]);}}
                          className="p-1 hover:bg-slate-100 rounded text-slate-400"><X size={14}/></button>
                      </div>
                    </div>

                    {/* Results banner */}
                    {copyResults.length > 0 && (
                      <div className="space-y-1">
                        {copyResults.map(r => (
                          <div key={r.grade} className={`flex items-center gap-2 text-xs px-3 py-1.5 rounded-lg ${
                            r.status==='ok' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
                          }`}>
                            <span>{r.status==='ok' ? '✅' : '❌'}</span>
                            <span>Grade {r.grade}: {r.message}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Grade rows */}
                    <div className="max-h-64 overflow-y-auto space-y-1.5 pr-1">
                      {ALL_GRADES.filter(g => g !== selGrade).map(grade => {
                        const t = copyTargets[grade] || {};
                        return (
                          <div key={grade}
                            className={`flex items-center gap-3 px-3 py-2 rounded-xl border transition-colors ${
                              t.checked ? 'bg-primary-50 border-primary-200' : 'bg-slate-50 border-slate-200'
                            }`}>
                            {/* Checkbox */}
                            <label className="flex items-center gap-2 cursor-pointer flex-shrink-0">
                              <input type="checkbox" checked={!!t.checked}
                                onChange={() => toggleGradeCheck(grade)}
                                className="w-4 h-4 accent-primary-600 rounded" />
                              <span className={`text-sm font-semibold w-20 ${t.checked ? 'text-primary-700' : 'text-slate-600'}`}>
                                Grade {grade}
                              </span>
                            </label>

                            {/* Subject dropdown — shown when checked */}
                            {t.loading ? (
                              <Loader2 size={14} className="animate-spin text-primary-400 flex-shrink-0"/>
                            ) : t.checked ? (
                              t.subjects.length > 0 ? (
                                <select
                                  className="flex-1 text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-primary-400"
                                  value={t.selectedSubjectId || ''}
                                  onChange={e => setGradeSubject(grade, e.target.value)}>
                                  <option value="">Select subject…</option>
                                  {t.subjects.map(s => (
                                    <option key={s.subject_id} value={s.subject_id}>
                                      {s.name}{s.is_configured ? ' ⟳ overwrite' : ''}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <span className="text-xs text-amber-600 flex-1">
                                  No subjects for this grade — assign in School Config → Subjects
                                </span>
                              )
                            ) : (
                              <span className="text-xs text-slate-400 flex-1">Click to select</span>
                            )}

                            {/* Auto-match badge */}
                            {t.checked && t.selectedSubjectId && (() => {
                              const match = (t.subjects||[]).find(s=>String(s.subject_id)===String(t.selectedSubjectId));
                              const isAutoMatch = match?.name.toLowerCase() === selSubject?.name.toLowerCase();
                              return isAutoMatch ? (
                                <span className="text-[10px] text-emerald-600 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-full flex-shrink-0">
                                  Auto-matched
                                </span>
                              ) : null;
                            })()}
                          </div>
                        );
                      })}
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-between pt-1 border-t border-slate-100">
                      <span className="text-xs text-slate-400">
                        {Object.values(copyTargets).filter(t=>t.checked&&t.selectedSubjectId).length} grade(s) selected
                      </span>
                      <div className="flex gap-2">
                        <button onClick={()=>{setShowCopyModal(false);setCopyResults([]);}}
                          className="px-4 py-1.5 border border-slate-200 text-xs text-slate-600 rounded-lg hover:bg-slate-50 transition-colors">
                          {copyResults.length ? 'Close' : 'Cancel'}
                        </button>
                        <button onClick={handleCopyAll}
                          disabled={copying || !Object.values(copyTargets).some(t=>t.checked&&t.selectedSubjectId)}
                          className="flex items-center gap-1.5 px-4 py-1.5 bg-primary-600 text-white text-xs font-semibold rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors">
                          {copying ? <Loader2 size={12} className="animate-spin"/> : null}
                          {copying
                            ? 'Copying…'
                            : `Copy to ${Object.values(copyTargets).filter(t=>t.checked&&t.selectedSubjectId).length || ''} Grade${Object.values(copyTargets).filter(t=>t.checked&&t.selectedSubjectId).length!==1?'s':''}`
                          }
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {selSubject.is_configured && (
                <>
                  {/* Tab switcher */}
                  <div className="flex border-b border-slate-100">
                    {[['strands','Strands & Sub-strands'],['weeks','Weeks']].map(([v,l]) => (
                      <button key={v} onClick={() => setActivePanel(v)}
                        className={`px-5 py-3 text-sm font-medium border-b-2 transition-all ${
                          activePanel===v ? 'border-primary-600 text-primary-700 bg-primary-50/40' : 'border-transparent text-slate-500 hover:text-slate-700'
                        }`}>{l}</button>
                    ))}
                  </div>

                  {loadingCfg ? (
                    <div className="flex items-center justify-center py-10"><Loader2 size={22} className="animate-spin text-primary-400"/></div>
                  ) : (
                    <div className="p-5">

                      {/* ── STRANDS panel ── */}
                      {activePanel === 'strands' && (
                        <div className="space-y-3">
                          <p className="text-xs text-slate-400">
                            Strands are the main sheet tabs (e.g. Listening, Speaking). Each strand has sub-strands as column headings.
                          </p>

                          {strands.length === 0 && (
                            <p className="text-sm text-slate-400 italic py-2">No strands yet. Add the first one below.</p>
                          )}

                          {strands.map(strand => {
                            const subs = substrands.filter(ss => ss.strand_id === strand.id);
                            const isExpanded = expandedStrand === strand.id;
                            const isEditing  = editStrand?.id === strand.id;

                            return (
                              <div key={strand.id} className="border border-slate-200 rounded-xl overflow-hidden">
                                {/* Strand header row */}
                                <div className="flex items-center gap-2 px-4 py-3 bg-slate-50/80 group">
                                  <button onClick={() => setExpandedStrand(isExpanded ? null : strand.id)}
                                    className="text-slate-400 hover:text-slate-600 transition-colors">
                                    {isExpanded ? <ChevDown size={15}/> : <ChevronRight size={15}/>}
                                  </button>

                                  {isEditing ? (
                                    <input autoFocus
                                      className="flex-1 text-sm border border-primary-300 rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary-400 bg-white"
                                      value={editStrand.name}
                                      onChange={e => setEditStrand(s=>({...s,name:e.target.value}))}
                                      onKeyDown={e => { if(e.key==='Enter') updateStrand(strand); if(e.key==='Escape') setEditStrand(null); }}
                                    />
                                  ) : (
                                    <span className="flex-1 text-sm font-semibold text-slate-700">{strand.name}</span>
                                  )}

                                  <span className="text-xs text-slate-400 bg-white border border-slate-200 px-2 py-0.5 rounded-full">
                                    {subs.length} sub-strand{subs.length!==1?'s':''}
                                  </span>

                                  {isEditing ? (
                                    <div className="flex gap-1">
                                      <button onClick={() => updateStrand(strand)} disabled={saving}
                                        className="p-1 hover:bg-emerald-50 text-emerald-600 rounded-lg"><Check size={13}/></button>
                                      <button onClick={() => setEditStrand(null)}
                                        className="p-1 hover:bg-slate-100 text-slate-400 rounded-lg"><X size={13}/></button>
                                    </div>
                                  ) : (
                                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 hover:opacity-100">
                                      <button onClick={() => setEditStrand({ id:strand.id, name:strand.name })}
                                        className="p-1 hover:bg-blue-50 text-blue-500 rounded-lg"><Edit2 size={13}/></button>
                                      <button onClick={() => deleteStrand(strand.id)}
                                        className="p-1 hover:bg-red-50 text-red-500 rounded-lg"><Trash2 size={13}/></button>
                                    </div>
                                  )}
                                </div>

                                {/* Sub-strands (expanded) */}
                                {isExpanded && (
                                  <div className="border-t border-slate-100 bg-white divide-y divide-slate-50">
                                    {subs.length === 0 && (
                                      <p className="px-5 py-2 text-xs text-slate-400 italic">No sub-strands yet</p>
                                    )}
                                    {subs.map(ss => {
                                      const isEditingSub = editSub?.id === ss.id;
                                      return (
                                        <div key={ss.id} className="flex items-center gap-2 px-5 py-2 group hover:bg-slate-50">
                                          <span className="w-1.5 h-1.5 rounded-full bg-primary-300 flex-shrink-0"/>
                                          {isEditingSub ? (
                                            <input autoFocus
                                              className="flex-1 text-xs border border-primary-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary-400 bg-white"
                                              value={editSub.name}
                                              onChange={e => setEditSub(s=>({...s,name:e.target.value}))}
                                              onKeyDown={e => { if(e.key==='Enter') updateSubstrand(ss); if(e.key==='Escape') setEditSub(null); }}
                                            />
                                          ) : (
                                            <span className="flex-1 text-xs text-slate-600">{ss.name}</span>
                                          )}
                                          {isEditingSub ? (
                                            <div className="flex gap-1">
                                              <button onClick={() => updateSubstrand(ss)} className="p-0.5 hover:bg-emerald-50 text-emerald-600 rounded"><Check size={11}/></button>
                                              <button onClick={() => setEditSub(null)} className="p-0.5 hover:bg-slate-100 text-slate-400 rounded"><X size={11}/></button>
                                            </div>
                                          ) : (
                                            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                              <button onClick={() => setEditSub({ id:ss.id, name:ss.name, strand_id:ss.strand_id })}
                                                className="p-1 hover:bg-blue-50 text-blue-400 rounded"><Edit2 size={11}/></button>
                                              <button onClick={() => deleteSubstrand(ss)}
                                                className="p-1 hover:bg-red-50 text-red-400 rounded"><Trash2 size={11}/></button>
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}

                                    {/* Add sub-strand */}
                                    {expandedStrand === strand.id && (
                                      <div className="flex items-center gap-2 px-5 py-2 bg-slate-50/50">
                                        <input
                                          className="flex-1 text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-primary-400"
                                          placeholder="New sub-strand name…"
                                          value={newSubName}
                                          onChange={e => setNewSubName(e.target.value)}
                                          onKeyDown={e => { if(e.key==='Enter') addSubstrand(strand.id); }}
                                        />
                                        <button onClick={() => addSubstrand(strand.id)} disabled={!newSubName.trim()||saving}
                                          className="px-3 py-1.5 text-xs font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-1">
                                          <Plus size={11}/> Add
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}

                          {/* Add strand */}
                          <div className="flex items-center gap-2 pt-1">
                            <input
                              className="flex-1 text-sm border border-slate-200 rounded-xl px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary-400"
                              placeholder="New strand name (e.g. Listening)"
                              value={newStrandName}
                              onChange={e => setNewStrandName(e.target.value)}
                              onKeyDown={e => { if(e.key==='Enter') addStrand(); }}
                            />
                            <button onClick={addStrand} disabled={!newStrandName.trim()||saving}
                              className="flex items-center gap-1.5 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-xl hover:bg-primary-700 disabled:opacity-50 transition-colors">
                              {saving ? <Loader2 size={13} className="animate-spin"/> : <Plus size={13}/>} Add Strand
                            </button>
                          </div>
                        </div>
                      )}

                      {/* ── WEEKS panel ── */}
                      {activePanel === 'weeks' && (
                        <div className="space-y-3">
                          {/* Header row: description + Import/Export/Template buttons */}
                          <div className="flex items-start justify-between gap-3">
                            <p className="text-xs text-slate-400 pt-0.5">
                              Weeks define the data entry windows. Select a Start Date and End Date — the date range is generated automatically.
                            </p>
                            <div className="flex items-center gap-1.5 shrink-0">
                              <input type="file" accept=".xlsx,.xls" ref={weekImportRef} className="hidden"
                                onChange={importWeeksFile}/>
                              <button onClick={downloadWeekTemplate}
                                title="Download week template"
                                className="flex items-center gap-1 px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 transition-colors">
                                <FileText size={12}/> Template
                              </button>
                              <button onClick={exportWeeks}
                                title="Export weeks to Excel"
                                className="flex items-center gap-1 px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 transition-colors">
                                <Download size={12}/> Export
                              </button>
                              <button onClick={()=>weekImportRef.current?.click()}
                                title="Import weeks from Excel"
                                className="flex items-center gap-1 px-2.5 py-1.5 text-xs border border-primary-200 rounded-lg text-primary-600 hover:bg-primary-50 transition-colors">
                                <Upload size={12}/> Import
                              </button>
                            </div>
                          </div>

                          {weeks.length === 0 && (
                            <p className="text-sm text-slate-400 italic py-2">No weeks configured yet.</p>
                          )}

                          {weeks.length > 0 && (
                            <div className="overflow-hidden rounded-xl border border-slate-200">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="bg-slate-50 text-xs text-slate-500 uppercase">
                                    <th className="px-3 py-2.5 text-left font-medium w-12">#</th>
                                    <th className="px-3 py-2.5 text-left font-medium">Week Name</th>
                                    <th className="px-3 py-2.5 text-left font-medium">Start Date</th>
                                    <th className="px-3 py-2.5 text-left font-medium">End Date</th>
                                    <th className="px-3 py-2.5 text-left font-medium">Date Range</th>
                                    <th className="px-3 py-2.5 text-left font-medium w-20">Actions</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-50">
                                  {weeks.map(w => {
                                    const isEdit = editWeek?.id === w.id;
                                    return (
                                      <tr key={w.id} className="hover:bg-slate-50 group">
                                        <td className="px-3 py-2 text-slate-500 font-mono text-xs">{w.week_number}</td>
                                        <td className="px-3 py-2">
                                          {isEdit
                                            ? <input className="w-full text-sm border border-primary-300 rounded px-2 py-1 focus:outline-none"
                                                value={editWeek.name} onChange={e=>setEditWeek(v=>({...v,name:e.target.value}))}/>
                                            : <span className="font-medium text-slate-700">{w.name}</span>}
                                        </td>
                                        <td className="px-3 py-2">
                                          {isEdit
                                            ? <input type="date"
                                                className="text-sm border border-primary-300 rounded px-2 py-1 focus:outline-none"
                                                value={editWeek.start_date||''} onChange={e=>setEditWeek(v=>({...v,start_date:e.target.value}))}/>
                                            : <span className="text-slate-500 text-xs">{w.start_date||'—'}</span>}
                                        </td>
                                        <td className="px-3 py-2">
                                          {isEdit
                                            ? <input type="date"
                                                className="text-sm border border-primary-300 rounded px-2 py-1 focus:outline-none"
                                                value={editWeek.end_date||''} onChange={e=>setEditWeek(v=>({...v,end_date:e.target.value}))}/>
                                            : <span className="text-slate-500 text-xs">{w.end_date||'—'}</span>}
                                        </td>
                                        <td className="px-3 py-2">
                                          {isEdit
                                            ? <span className="text-xs text-slate-400 italic">
                                                {buildDateRange(editWeek.start_date, editWeek.end_date) || '—'}
                                              </span>
                                            : <span className="text-slate-500 text-xs">{w.date_range||'—'}</span>}
                                        </td>
                                        <td className="px-3 py-2">
                                          {isEdit ? (
                                            <div className="flex gap-1">
                                              <button onClick={updateWeek} disabled={saving}
                                                className="p-1 hover:bg-emerald-50 text-emerald-600 rounded"><Check size={13}/></button>
                                              <button onClick={()=>setEditWeek(null)}
                                                className="p-1 hover:bg-slate-100 text-slate-400 rounded"><X size={13}/></button>
                                            </div>
                                          ) : (
                                            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                              <button onClick={()=>setEditWeek({...w})}
                                                className="p-1 hover:bg-blue-50 text-blue-500 rounded"><Edit2 size={13}/></button>
                                              <button onClick={()=>deleteWeek(w.id)}
                                                className="p-1 hover:bg-red-50 text-red-500 rounded"><Trash2 size={13}/></button>
                                            </div>
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )}

                          {/* Add week row */}
                          <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
                            <p className="text-xs text-slate-400 mb-2 font-medium">Add New Week</p>
                            <div className="flex items-center gap-2 flex-wrap">
                              <input
                                className="flex-1 min-w-32 text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary-400"
                                placeholder="Week name (e.g. Week 3)"
                                value={newWeek.name}
                                onChange={e => setNewWeek(w=>({...w,name:e.target.value}))}
                              />
                              <div className="flex items-center gap-1">
                                <span className="text-xs text-slate-400">Start</span>
                                <input type="date"
                                  className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary-400"
                                  value={newWeek.start_date}
                                  onChange={e => setNewWeek(w=>({...w,start_date:e.target.value}))}
                                />
                              </div>
                              <div className="flex items-center gap-1">
                                <span className="text-xs text-slate-400">End</span>
                                <input type="date"
                                  className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary-400"
                                  value={newWeek.end_date}
                                  onChange={e => setNewWeek(w=>({...w,end_date:e.target.value}))}
                                />
                              </div>
                              {(newWeek.start_date || newWeek.end_date) && (
                                <span className="text-xs text-primary-600 font-medium bg-primary-50 px-2 py-1 rounded">
                                  {buildDateRange(newWeek.start_date, newWeek.end_date)}
                                </span>
                              )}
                              <button onClick={addWeek} disabled={!newWeek.name.trim()||saving}
                                className="flex items-center gap-1.5 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors">
                                {saving ? <Loader2 size={13} className="animate-spin"/> : <Plus size={13}/>} Add Week
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function LearningTrackers() {
  const { user, isSuperAdmin, isManagement } = useAuth();
  const { yearId } = useContext(YearContext);
  const [view, setView] = useState('dashboard'); // 'entry' | 'dashboard' | 'setup'
  const hasSchoolView = SCHOOL_VIEW_ROLES.has(user?.role);

  // ── Selectors ─────────────────────────────────────────
  const [classes,      setClasses]      = useState([]);
  const [myScope, setMyScope] = useState(null); // null until loaded; scope==='school' means no filter
  // true once scope resolves to a non-school-wide role (class/subject teacher)
  const isTeacherScope = myScope !== null && myScope.scope !== 'school';
  const [subjects,     setSubjects]     = useState([]);
  const [selClass,     setSelClass]     = useState('');
  const [selGrade,     setSelGrade]     = useState('');
  const [selSubject,   setSelSubject]   = useState('');
  const [selWeek,      setSelWeek]      = useState('');
  const [config,       setConfig]       = useState(null);  // { strands, substrands, weeks }

  // ── Entry form ────────────────────────────────────────
  const [strandTab, setStrandTab]   = useState(0);
  const [entryData, setEntryData]   = useState(null); // { students, strands, substrands, entryMap }
  const [localGrades, setLocalGrades] = useState({}); // { studentId-substrandId : grade }
  const [loadingEntry, setLoadingEntry] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty,  setDirty]  = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // ── My-classes card picker (entry view) ───────────────
  const [myClasses, setMyClasses]               = useState(null); // null until first load
  const [loadingMyClasses, setLoadingMyClasses] = useState(false);
  const [cardFilter, setCardFilter]             = useState({ q:'', grade:'all', subject:'all', status:'all' });
  const [entrySel, setEntrySel]                 = useState(null); // opened card {classId, trackerId, grade, section, subjectName, weekId}
  const [entryWeeks, setEntryWeeks]             = useState([]);   // week list for the opened card's subject

  // ── Class overview (teacher dashboard) ────────────────
  const [overviewClassId, setOverviewClassId]     = useState(null);
  const [classDash, setClassDash]                 = useState(null);
  const [loadingClassDash, setLoadingClassDash]   = useState(false);
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') setIsFullscreen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // ── Dashboard ─────────────────────────────────────────
  const [dashData,      setDashData]      = useState(null);
  const [loadingDash,   setLoadingDash]   = useState(false);
  const [summary,       setSummary]       = useState(null);
  const [loadingSummary,setLoadingSummary]= useState(false);
  const [chartsData,    setChartsData]    = useState(null);
  const [loadingCharts, setLoadingCharts] = useState(false);
  const [chartsError,   setChartsError]   = useState(null);

  // Load classes and resolve scope on mount
  useEffect(() => {
    schoolConfigAPI.getClasses().then(r => setClasses(r.data)).catch(() => {});
    rbacAPI.getMyScope('learning_trackers')
      .then(r => setMyScope(r.data))
      .catch(() => setMyScope({ scope: 'school', gradeList: [], sectionMap: [], subjectMap: [] }));
  }, []);

  // When grade changes, filter classes and load subjects
  useEffect(() => {
    if (!selGrade) { setSubjects([]); setSelSubject(''); return; }
    trackerAPI.getSubjects({ grade: selGrade }).then(r => {
      let data = r.data;
      // Restrict to assigned subjects when scope is own_subjects
      if (myScope && myScope.scope === 'own_subjects' && myScope.subjectMap?.length) {
        const allowed = new Set(
          myScope.subjectMap
            .filter(s => s.grade === selGrade)
            .map(s => s.subject_id)
        );
        data = data.filter(s => allowed.has(s.subject_id));
      }
      setSubjects(data);
      setSelSubject('');
      setConfig(null);
    }).catch(() => {});
  }, [selGrade]);

  // When class changes, derive grade
  useEffect(() => {
    const cls = classes.find(c => String(c.id) === String(selClass));
    if (cls?.grade) setSelGrade(cls.grade);
  }, [selClass, classes]);

  // When subject changes, load its config
  useEffect(() => {
    if (!selSubject) { setConfig(null); setSelWeek(''); return; }
    trackerAPI.getSubjectConfig(selSubject).then(r => {
      setConfig(r.data);
      setSelWeek('');
      setEntryData(null);
      setLocalGrades({});
    }).catch(() => {});
  }, [selSubject]);

  // Load the my-classes cards when the entry view opens
  const loadMyClasses = useCallback(async () => {
    setLoadingMyClasses(true);
    try {
      const r = await trackerAPI.getMyClasses();
      setMyClasses(r.data);
    } catch { toast.error('Failed to load your classes'); }
    finally { setLoadingMyClasses(false); }
  }, []);

  useEffect(() => {
    // Entry view always needs the cards; class-scoped users also need them for the dashboard
    if (!isManagement && myClasses === null && (view === 'entry' || !hasSchoolView)) loadMyClasses();
  }, [view, isManagement, hasSchoolView, myClasses, loadMyClasses]);

  // Open a class card → load its entry grid (current week pre-selected)
  const openCard = (card) => {
    if (!card.week) { toast.error('No weeks configured for this subject yet'); return; }
    setEntrySel({
      classId: card.classId, trackerId: card.trackerId,
      grade: card.grade, section: card.section, subjectName: card.subjectName,
      weekId: card.week.id,
    });
    setEntryWeeks([]);
    trackerAPI.getSubjectConfig(card.trackerId)
      .then(r => setEntryWeeks(r.data.weeks || []))
      .catch(() => {});
  };

  const closeCard = () => {
    if (dirty && !window.confirm('You have unsaved changes. Discard them?')) return;
    setEntrySel(null);
    setEntryData(null);
    setDirty(false);
    loadMyClasses(); // refresh status badges
  };

  // Load entries for the opened card + selected week
  const loadEntries = useCallback(async () => {
    if (!entrySel) return;
    setLoadingEntry(true);
    try {
      const r = await trackerAPI.getEntries({ subjectId: entrySel.trackerId, classId: entrySel.classId, weekId: entrySel.weekId });
      setEntryData(r.data);
      // Initialise local grades from saved data
      const map = {};
      Object.entries(r.data.entryMap || {}).forEach(([stuId, subMap]) => {
        Object.entries(subMap).forEach(([subId, grade]) => {
          map[`${stuId}-${subId}`] = grade;
        });
      });
      setLocalGrades(map);
      setDirty(false);
      setStrandTab(0);
    } catch { toast.error('Failed to load entries'); }
    finally { setLoadingEntry(false); }
  }, [entrySel]);

  useEffect(() => { loadEntries(); }, [loadEntries]);

  const handleGradeChange = (studentId, substrandId, grade) => {
    setLocalGrades(prev => ({ ...prev, [`${studentId}-${substrandId}`]: grade }));
    setDirty(true);
  };

  const handleSave = async () => {
    if (!entrySel || !entryData) return;
    setSaving(true);
    try {
      const entries = Object.entries(localGrades)
        .filter(([, g]) => g)
        .map(([key, grade]) => {
          const [studentId, substrandId] = key.split('-');
          return { studentId: parseInt(studentId), substrandId: parseInt(substrandId), grade };
        });
      const r = await trackerAPI.saveEntries({ entries, weekId: parseInt(entrySel.weekId), classId: parseInt(entrySel.classId), academicYearId: yearId });
      toast.success(r.data.message);
      setDirty(false);
      // Refresh card status badges in the background
      trackerAPI.getMyClasses().then(r2 => setMyClasses(r2.data)).catch(() => {});
    } catch (e) { toast.error(e.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  // Load dashboard
  const loadDashboard = useCallback(async () => {
    if (!selSubject) return;
    setLoadingDash(true);
    try {
      const r = await trackerAPI.getDashboard({ subjectId: selSubject, classId: selClass||undefined, weekId: selWeek||undefined });
      setDashData(r.data);
    } catch { toast.error('Failed to load dashboard'); }
    finally { setLoadingDash(false); }
  }, [selSubject, selClass, selWeek]);

  // Management users always see the dashboard regardless of the `view` state
  const isDashboardVisible = view === 'dashboard' || isManagement;

  // ── Class overview: pick default class + load its data (class-scoped users) ──
  useEffect(() => {
    if (hasSchoolView || !isDashboardVisible || !myClasses?.length) return;
    const distinct = [...new Map(myClasses.map(c => [c.classId, c])).values()];
    if (!overviewClassId || !distinct.some(c => c.classId === overviewClassId)) {
      setOverviewClassId(distinct[0].classId);
    }
  }, [hasSchoolView, isDashboardVisible, myClasses, overviewClassId]);

  useEffect(() => {
    if (hasSchoolView || !overviewClassId) return;
    setLoadingClassDash(true);
    trackerAPI.getClassDashboard({ classId: overviewClassId })
      .then(r => setClassDash(r.data))
      .catch(() => toast.error('Failed to load class overview'))
      .finally(() => setLoadingClassDash(false));
  }, [hasSchoolView, overviewClassId]);

  useEffect(() => { if (isDashboardVisible) loadDashboard(); }, [isDashboardVisible, loadDashboard]);

  const loadSummary = useCallback(async () => {
    if (isTeacherScope || !hasSchoolView) return;
    setLoadingSummary(true);
    try {
      const r = await trackerAPI.getSchoolSummary(yearId ? { academicYearId: yearId } : {});
      setSummary(r.data);
    } catch { /* summary is optional — fail silently */ }
    finally { setLoadingSummary(false); }
  }, [yearId, isTeacherScope, hasSchoolView]);

  useEffect(() => { if (isDashboardVisible) loadSummary(); }, [isDashboardVisible, loadSummary]);

  const loadCharts = useCallback(async () => {
    if (isTeacherScope || !hasSchoolView) return;
    setLoadingCharts(true);
    setChartsError(null);
    try {
      const r = await trackerAPI.getDashboardCharts(yearId ? { academicYearId: yearId } : {});
      setChartsData(r.data);
    } catch(e) {
      const msg = e.response?.data?.error || e.message || 'Failed to load analytics';
      console.error('[Charts]', msg);
      setChartsError(msg);
    }
    finally { setLoadingCharts(false); }
  }, [yearId, isTeacherScope, hasSchoolView]);

  useEffect(() => { if (isDashboardVisible) loadCharts(); }, [isDashboardVisible, loadCharts]);

  // ── Derived display values ────────────────────────────
  const scopedGrades = myScope && myScope.scope !== 'school' && myScope.gradeList?.length
    ? myScope.gradeList
    : null;
  // Class-scoped users: restrict dashboard filters to their assigned classes/subjects
  const allowedGradeSet   = !hasSchoolView && myClasses ? new Set(myClasses.map(c => c.grade))     : null;
  const allowedClassSet   = !hasSchoolView && myClasses ? new Set(myClasses.map(c => c.classId))   : null;
  const allowedTrackerSet = !hasSchoolView && myClasses ? new Set(myClasses.map(c => c.trackerId)) : null;
  const grades = [...new Set(classes.map(c => c.grade))]
    .filter(g => (!scopedGrades || scopedGrades.includes(g)) && (!allowedGradeSet || allowedGradeSet.has(g)))
    .sort();
  const classesForGrade = classes.filter(c =>
    c.grade === selGrade && (!allowedClassSet || allowedClassSet.has(c.id))
  );
  const weeks = config?.weeks || [];
  const strands = entryData?.strands || [];
  const currentStrand = strands[strandTab];
  const substrands = (entryData?.substrands || []).filter(ss => ss.strand_id === currentStrand?.id);
  const students = entryData?.students || [];

  const selWeekObj  = weeks.find(w => String(w.id) === String(selWeek));
  const selSubjObj  = subjects.find(s => String(s.id) === String(selSubject));
  const selClassObj = classes.find(c => String(c.id) === String(selClass));

  // Management users only see dashboard
  const canEdit = !isManagement;

  return (
    <div className="space-y-5 animate-fade-in">

      {/* ── Header ── */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-display font-bold text-slate-800 flex items-center gap-2">
            <BookOpen size={22} className="text-primary-600" /> Learning Trackers
          </h2>
          <p className="text-slate-500 text-sm mt-0.5">Weekly student performance tracking</p>
        </div>
        {canEdit && (
          <div className="flex gap-1 bg-white rounded-xl p-1 shadow-card border border-slate-100">
            {[
              ['dashboard', 'Dashboard',  BarChart3,      false],
              ['entry',     'Grade Entry', ClipboardCheck, !canEdit],
              ['setup',     'Setup',      Settings,       !isSuperAdmin],
            ].filter(([,,, hide]) => !hide).map(([v,l,Icon]) => (
              <button key={v} onClick={()=>setView(v)}
                className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-all ${view===v?'bg-primary-600 text-white shadow-sm':'text-slate-600 hover:text-slate-800'}`}>
                <Icon size={15}/>{l}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── Grade Legend (hidden on dashboard — shown after summary) ── */}
      {!isDashboardVisible && (
        <div className="flex flex-wrap gap-2">
          {GRADES.map(g => (
            <div key={g} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-medium ${GRADE_CFG[g].bg} ${GRADE_CFG[g].text} ${GRADE_CFG[g].border}`}>
              <span className="font-bold">{g}</span>
              <span className="opacity-75">–</span>
              <span>{GRADE_CFG[g].label}</span>
              <span className="opacity-60">({GRADE_CFG[g].range})</span>
            </div>
          ))}
        </div>
      )}

      {/* ── FINAL OVERALL SUMMARY (school-wide roles only) ── */}
      {isDashboardVisible && hasSchoolView && !isTeacherScope && (loadingSummary || summary) && (
        <div className="glass-card p-5 space-y-4">
          <div className="flex items-center gap-2">
            <Activity size={16} className="text-primary-600"/>
            <h3 className="text-sm font-bold text-slate-800">Final Overall Summary</h3>
            {loadingSummary && <Loader2 size={13} className="animate-spin text-slate-400 ml-1"/>}
          </div>

          {summary && (() => {
            const s = summary;
            const spiColor = s.spi >= 75 ? 'text-emerald-600' : s.spi >= 50 ? 'text-amber-600' : 'text-red-500';
            const spiRing  = s.spi >= 75 ? 'ring-emerald-200 bg-emerald-50' : s.spi >= 50 ? 'ring-amber-200 bg-amber-50' : 'ring-red-200 bg-red-50';

            const KPI = ({ icon: Icon, label, value, sub, color='text-slate-700', bg='bg-white', ring='ring-slate-100', bold=false }) => (
              <div className={`flex flex-col gap-1 p-3.5 rounded-xl ring-1 ${bg} ${ring} min-w-0`}>
                <div className="flex items-center gap-1.5 text-slate-400">
                  <Icon size={13}/>
                  <span className="text-[11px] font-medium uppercase tracking-wide leading-none truncate">{label}</span>
                </div>
                <div className={`text-2xl font-bold leading-none ${color} ${bold ? 'text-lg' : ''}`}>{value}</div>
                {sub && <div className="text-[11px] text-slate-400 truncate">{sub}</div>}
              </div>
            );

            return (
              <div className="space-y-3">
                <div>
                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-2">Students</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                    <KPI icon={Users}         label="Total Students"        value={s.totalStudents?.toLocaleString() ?? '—'} sub={s.gradedStudents ? `${s.gradedStudents} graded` : undefined}/>
                    <KPI icon={TrendingUp}    label="School Average %"       value={s.schoolAvgPct != null ? `${s.schoolAvgPct}%` : '—'} color="text-primary-600"/>
                    <KPI icon={UserCheck}     label="Above Target"           value={s.aboveTarget ?? '—'} sub="P / E grade" color="text-emerald-600" bg="bg-emerald-50" ring="ring-emerald-100"/>
                    <KPI icon={TrendingDown}  label="Below Target"           value={s.belowTarget  ?? '—'} sub="W / D grade" color="text-red-500"     bg="bg-red-50"     ring="ring-red-100"/>
                    <KPI icon={AlertTriangle} label="Requiring Intervention" value={s.intervention ?? '—'} sub="W grade only" color="text-rose-600"   bg="bg-rose-50"   ring="ring-rose-100"/>
                    <KPI icon={ShieldAlert}   label="Academic Risk"          value={s.academicRisk ?? '—'} sub="D grade only" color="text-orange-600" bg="bg-orange-50" ring="ring-orange-100"/>
                  </div>
                </div>
                <div>
                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-2">Attendance &amp; Staff</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                    <KPI icon={CalendarCheck} label="Avg Attendance %"      value={s.avgAttendancePct != null ? `${s.avgAttendancePct}%` : '—'} color={s.avgAttendancePct >= 90 ? 'text-emerald-600' : s.avgAttendancePct >= 75 ? 'text-amber-600' : 'text-red-500'}/>
                    <KPI icon={GraduationCap} label="Total Teachers"         value={s.totalTeachers?.toLocaleString() ?? '—'}/>
                    <KPI icon={Star}          label="Avg Observation Score"  value={s.avgObsScore != null ? s.avgObsScore.toFixed(2) : '—'} sub="out of 5.00" color="text-amber-600"/>
                    <KPI icon={UserX}         label="Teachers Req. Support"  value={s.teachersRequiringSupport ?? '—'} sub="obs avg < 3.0" color={s.teachersRequiringSupport > 0 ? 'text-red-500' : 'text-emerald-600'} bg={s.teachersRequiringSupport > 0 ? 'bg-red-50' : 'bg-emerald-50'} ring={s.teachersRequiringSupport > 0 ? 'ring-red-100' : 'ring-emerald-100'}/>
                    <KPI icon={Clock}         label="Pending Follow-ups"     value={s.pendingFollowups ?? '—'} color={s.pendingFollowups > 0 ? 'text-amber-600' : 'text-emerald-600'} bg={s.pendingFollowups > 0 ? 'bg-amber-50' : 'bg-emerald-50'} ring={s.pendingFollowups > 0 ? 'ring-amber-100' : 'ring-emerald-100'}/>
                  </div>
                </div>
                <div>
                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-2">Subjects &amp; Performance</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <KPI icon={Trophy}        label="Best Subject"           value={s.bestSubject    ?? '—'} color="text-emerald-600" bg="bg-emerald-50" ring="ring-emerald-100" bold/>
                    <KPI icon={AlertCircle}   label="Weakest Subject"        value={s.weakestSubject ?? '—'} color="text-red-500"    bg="bg-red-50"     ring="ring-red-100" bold/>
                    <KPI icon={ClipboardList} label="Total Observations"     value={s.totalObservations?.toLocaleString() ?? '—'} sub="all years"/>
                    <div className={`flex flex-col items-center justify-center p-3.5 rounded-xl ring-1 ${spiRing}`}>
                      <div className="flex items-center gap-1.5 text-slate-400 mb-1">
                        <Activity size={13}/>
                        <span className="text-[11px] font-medium uppercase tracking-wide">School Performance Index</span>
                      </div>
                      <div className={`text-3xl font-black ${spiColor}`}>{s.spi != null ? `${s.spi}` : '—'}</div>
                      <div className="text-[11px] text-slate-400 mt-0.5">out of 100</div>
                      <div className="mt-1.5 w-full bg-white/60 rounded-full h-1.5 overflow-hidden">
                        <div className={`h-full rounded-full transition-all ${s.spi >= 75 ? 'bg-emerald-400' : s.spi >= 50 ? 'bg-amber-400' : 'bg-red-400'}`}
                          style={{ width: `${s.spi || 0}%` }}/>
                      </div>
                      <div className="text-[10px] text-slate-400 mt-1">
                        {s.spi >= 75 ? 'Good' : s.spi >= 50 ? 'Average' : s.spi > 0 ? 'Needs Improvement' : 'No data'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* ── Grade Legend (dashboard / management: shown after summary) ── */}
      {isDashboardVisible && (
        <div className="flex flex-wrap gap-2">
          {GRADES.map(g => (
            <div key={g} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-medium ${GRADE_CFG[g].bg} ${GRADE_CFG[g].text} ${GRADE_CFG[g].border}`}>
              <span className="font-bold">{g}</span>
              <span className="opacity-75">–</span>
              <span>{GRADE_CFG[g].label}</span>
              <span className="opacity-60">({GRADE_CFG[g].range})</span>
            </div>
          ))}
        </div>
      )}

      {/* ── Filter Row (dashboard views only — entry view uses class cards) ── */}
      {!(view === 'entry' && canEdit) && (
      <div className="glass-card p-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div>
            <label className="text-xs font-medium text-slate-500 uppercase tracking-wide block mb-1">Grade</label>
            <select className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary-400"
              value={selGrade} onChange={e=>{setSelGrade(e.target.value); setSelClass('');}}>
              <option value="">Select grade…</option>
              {grades.map(g=><option key={g} value={g}>{g}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 uppercase tracking-wide block mb-1">Class / Section</label>
            <select className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary-400"
              value={selClass} onChange={e=>setSelClass(e.target.value)} disabled={!selGrade}>
              <option value="">Select class…</option>
              {classesForGrade.map(c=><option key={c.id} value={c.id}>{c.grade} {c.section ? `– ${c.section}` : ''}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 uppercase tracking-wide block mb-1">Subject</label>
            <select className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary-400"
              value={selSubject} onChange={e=>setSelSubject(e.target.value)} disabled={!selGrade}>
              <option value="">Select subject…</option>
              {subjects.filter(s => !allowedTrackerSet || allowedTrackerSet.has(s.id)).map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 uppercase tracking-wide block mb-1">Week</label>
            <select className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary-400"
              value={selWeek} onChange={e=>setSelWeek(e.target.value)} disabled={!selSubject}>
              <option value="">Select week…</option>
              {weeks.map(w=><option key={w.id} value={w.id}>{w.name}{w.date_range ? ` (${w.date_range})` : ''}</option>)}
            </select>
          </div>
        </div>
      </div>
      )}

      {/* ═══ ENTRY FORM VIEW ═══ */}
      {(view==='entry' && canEdit) && (
        <>
          {!entrySel ? (
            /* ── MY CLASSES card picker ── */
            (loadingMyClasses || myClasses === null) ? (
              <div className="glass-card p-12 flex items-center justify-center">
                <Loader2 size={28} className="animate-spin text-primary-500" />
              </div>
            ) : myClasses.length === 0 ? (
              <div className="glass-card p-12 text-center">
                <BookOpen size={40} className="text-slate-300 mx-auto mb-3" />
                <p className="text-slate-400 font-medium">No classes available for grade entry</p>
                <p className="text-slate-300 text-sm mt-2">
                  Ask your admin to assign your classes and subjects in User Access Control,
                  and to configure tracker subjects in Setup.
                </p>
              </div>
            ) : (() => {
              const cardStatus = c => !c.week ? 'none'
                : (c.totalStudents > 0 && c.enteredStudents >= c.totalStudents) ? 'done' : 'pending';
              const cardGrades   = [...new Set(myClasses.map(c => c.grade))];
              const cardSubjects = [...new Set(myClasses.map(c => c.subjectName))].sort();
              const q = cardFilter.q.trim().toLowerCase();
              const visibleCards = myClasses.filter(c => {
                if (cardFilter.status  !== 'all' && cardStatus(c)  !== cardFilter.status)  return false;
                if (cardFilter.grade   !== 'all' && c.grade        !== cardFilter.grade)   return false;
                if (cardFilter.subject !== 'all' && c.subjectName  !== cardFilter.subject) return false;
                if (q && !`${c.grade} ${c.section||''} ${c.subjectName}`.toLowerCase().includes(q)) return false;
                return true;
              });
              const showFilters = true;
              const Pill = ({ on, label, onClick }) => (
                <button onClick={onClick}
                  className={`px-3 py-1 rounded-full text-xs font-medium border transition-all ${
                    on ? 'bg-primary-50 text-primary-700 border-primary-300'
                       : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'}`}>
                  {label}
                </button>
              );
              return (
                <div className="space-y-3">
                  {showFilters && (
                    <div className="glass-card p-4 space-y-3">
                      <div className="flex flex-wrap gap-3 items-center">
                        <div className="flex items-center gap-2 flex-1 min-w-[200px] bg-white border border-slate-200 rounded-xl px-3 py-2">
                          <Search size={14} className="text-slate-400 flex-shrink-0"/>
                          <input
                            value={cardFilter.q}
                            onChange={e => setCardFilter(f => ({ ...f, q: e.target.value }))}
                            placeholder="Search class or subject…"
                            className="flex-1 text-sm outline-none bg-transparent min-w-0"
                          />
                        </div>
                        <div className="flex gap-1.5">
                          {[['all','All'],['pending','Pending'],['done','Done']].map(([v,l]) => (
                            <Pill key={v} on={cardFilter.status===v} label={l}
                              onClick={() => setCardFilter(f => ({ ...f, status: v }))}/>
                          ))}
                        </div>
                      </div>
                      {cardGrades.length > 1 && (
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-xs font-medium text-slate-400 uppercase tracking-wide w-14 flex-shrink-0">Grade</span>
                          <Pill on={cardFilter.grade==='all'} label="All" onClick={() => setCardFilter(f => ({ ...f, grade: 'all' }))}/>
                          {cardGrades.map(g => (
                            <Pill key={g} on={cardFilter.grade===g} label={g} onClick={() => setCardFilter(f => ({ ...f, grade: g }))}/>
                          ))}
                        </div>
                      )}
                      {cardSubjects.length > 1 && (
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-xs font-medium text-slate-400 uppercase tracking-wide w-14 flex-shrink-0">Subject</span>
                          <Pill on={cardFilter.subject==='all'} label="All" onClick={() => setCardFilter(f => ({ ...f, subject: 'all' }))}/>
                          {cardSubjects.map(s => (
                            <Pill key={s} on={cardFilter.subject===s} label={s} onClick={() => setCardFilter(f => ({ ...f, subject: s }))}/>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  <p className="text-xs text-slate-400">
                    Showing {visibleCards.length} of {myClasses.length} classes · status is for the current week · click a card to start entering grades
                  </p>
                  {visibleCards.length === 0 ? (
                    <div className="glass-card p-10 text-center text-slate-400 text-sm">No classes match these filters</div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                      {visibleCards.map(c => {
                        const st = cardStatus(c);
                        return (
                          <button
                            key={`${c.classId}-${c.subjectId}`}
                            onClick={() => openCard(c)}
                            className="bg-white rounded-2xl border border-slate-200 p-4 text-left hover:border-primary-300 hover:shadow-md transition-all group"
                          >
                            <div className="flex items-start justify-between gap-2 mb-1.5">
                              <span className="text-sm font-bold text-slate-700 group-hover:text-primary-700">
                                {c.grade}{c.section ? ` – ${c.section}` : ''}
                              </span>
                              {(c.weeks?.length > 0) && (
                                <div className="flex gap-1 flex-wrap justify-end max-w-[55%]">
                                  {c.weeks.map(w => {
                                    const wDone = c.totalStudents > 0 && w.entered >= c.totalStudents;
                                    const wPart = !wDone && w.entered > 0;
                                    const wLate = !wDone && !wPart && w.isDue; // due but nothing entered
                                    return (
                                      <span
                                        key={w.id}
                                        title={`${w.name}${w.dateRange ? ` (${w.dateRange})` : ''} · ${w.entered}/${c.totalStudents} students${wLate ? ' · pending' : ''}`}
                                        className={`w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-bold flex-shrink-0 ${
                                          wDone ? 'bg-emerald-200 text-emerald-800'
                                          : wPart ? 'bg-amber-200 text-amber-800'
                                          : wLate ? 'bg-red-200 text-red-800'
                                          : 'bg-slate-100 text-slate-400'
                                        } ${w.isCurrent ? 'ring-1 ring-primary-400' : ''}`}
                                      >
                                        {w.label}
                                      </span>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                            <p className="text-sm text-slate-600">{c.subjectName}</p>
                            <div className="flex items-end justify-between gap-2 mt-1">
                              <p className="text-xs text-slate-400">
                                {c.week
                                  ? `${c.week.name}${c.week.dateRange ? ` · ${c.week.dateRange}` : ''} · ${c.enteredStudents}/${c.totalStudents} students`
                                  : 'No weeks configured'}
                              </p>
                              {st === 'done' ? (
                                <span className="flex-shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-200">done</span>
                              ) : st === 'none' ? (
                                <span className="flex-shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-50 text-slate-400 border border-slate-200">no weeks</span>
                              ) : c.enteredStudents === 0 ? (
                                <span className="flex-shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-red-50 text-red-500 border border-red-200">not started</span>
                              ) : (
                                <span className="flex-shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-200">
                                  {c.totalStudents - c.enteredStudents} pending
                                </span>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })()
          ) : loadingEntry ? (
            <div className="glass-card p-12 flex items-center justify-center">
              <Loader2 size={28} className="animate-spin text-primary-500" />
            </div>
          ) : !entryData ? null : (() => {
            const entryCard = (
            <div className={
              isFullscreen
                ? 'fixed inset-0 z-[9999] bg-white flex flex-col shadow-2xl overflow-hidden'
                : 'glass-card overflow-hidden'
            }>
              {/* Form header */}
              <div className={`px-5 ${isFullscreen ? 'py-2' : 'py-4'} border-b border-slate-100 flex items-center justify-between bg-slate-50/60 flex-shrink-0`}>
                <div className="flex items-center gap-3 min-w-0">
                  <button
                    onClick={closeCard}
                    title="Back to my classes"
                    className="p-2 rounded-xl border border-slate-200 text-slate-500 hover:text-primary-600 hover:border-primary-300 hover:bg-primary-50 transition-colors flex-shrink-0"
                  >
                    <ArrowLeft size={15}/>
                  </button>
                  <div className="min-w-0">
                    <h3 className="text-sm font-bold text-slate-700 truncate">
                      {entrySel.subjectName} · {entrySel.grade}{entrySel.section ? ` ${entrySel.section}` : ''}
                    </h3>
                    <p className="text-xs text-slate-400 mt-0.5">{students.length} students</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {entryWeeks.length > 0 && (
                    <select
                      value={entrySel.weekId}
                      onChange={e => {
                        if (dirty && !window.confirm('You have unsaved changes. Discard them and switch week?')) return;
                        setEntrySel(s => ({ ...s, weekId: parseInt(e.target.value) }));
                      }}
                      className="text-sm border border-slate-200 rounded-xl px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary-400"
                    >
                      {entryWeeks.map(w => (
                        <option key={w.id} value={w.id}>{w.name}{w.date_range ? ` (${w.date_range})` : ''}</option>
                      ))}
                    </select>
                  )}
                  <button
                    onClick={() => setIsFullscreen(f => !f)}
                    title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                    className="p-2 rounded-xl border border-slate-200 text-slate-500 hover:text-primary-600 hover:border-primary-300 hover:bg-primary-50 transition-colors"
                  >
                    {isFullscreen ? <Minimize2 size={15}/> : <Maximize2 size={15}/>}
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving || !dirty}
                    className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold rounded-xl transition-colors disabled:opacity-50 shadow-sm"
                  >
                    {saving ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>}
                    {saving ? 'Saving…' : dirty ? 'Save Grades' : 'All Saved'}
                  </button>
                </div>
              </div>

              {/* Strand tabs */}
              {strands.length > 0 && (
                <div className="flex border-b border-slate-100 bg-white overflow-x-auto">
                  {strands.map((strand, idx) => (
                    <button
                      key={strand.id}
                      onClick={() => setStrandTab(idx)}
                      className={`px-5 py-3 text-sm font-medium whitespace-nowrap transition-all border-b-2 ${
                        strandTab === idx
                          ? 'border-primary-600 text-primary-700 bg-primary-50/40'
                          : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      {strand.name}
                    </button>
                  ))}
                </div>
              )}

              {/* Entry table — header locked, student rows scroll */}
              {currentStrand && (
                <div
                  className="overflow-auto"
                  style={isFullscreen
                    ? { flex: 1, minHeight: 0 }
                    : { maxHeight: 'calc(100vh - 360px)', minHeight: '200px' }}
                >
                  <table className="w-full text-sm border-separate border-spacing-0">
                    {/* ── Sticky header — locked while student rows scroll ── */}
                    <thead className="sticky top-0 z-20">
                      <tr className="bg-slate-50">
                        {/* Corner cell: sticky on BOTH top and left axes */}
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide sticky left-0 top-0 bg-slate-50 z-30 min-w-[180px] border-b border-r border-slate-200 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)]">
                          Student Name
                        </th>
                        {substrands.map(ss => (
                          <th key={ss.id} className="px-3 py-3 text-center text-xs font-semibold text-slate-600 min-w-[185px] bg-slate-50 border-b border-slate-200">
                            <div className="leading-tight">{ss.name}</div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {students.length === 0 && (
                        <tr><td colSpan={substrands.length+1} className="text-center py-8 text-slate-400">No students in this class</td></tr>
                      )}
                      {students.map((student, idx) => (
                        <tr key={student.id} className={`${idx%2===0?'bg-white':'bg-slate-50/30'} hover:bg-primary-50/20 transition-colors`}>
                          {/* Student name — sticky left, inherits row background */}
                          <td className="px-4 py-2.5 sticky left-0 bg-inherit z-10 border-r border-slate-100 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.06)]">
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center text-xs font-bold text-primary-700 flex-shrink-0">
                                {student.name?.[0]}
                              </div>
                              <span className="font-medium text-slate-700 text-xs truncate max-w-[140px]">{student.name}</span>
                            </div>
                          </td>
                          {substrands.map(ss => (
                            <td key={ss.id} className="px-3 py-2.5 text-center">
                              <div className="flex items-center justify-center">
                                <GradeCell
                                  value={localGrades[`${student.id}-${ss.id}`] || ''}
                                  onChange={g => handleGradeChange(student.id, ss.id, g)}
                                />
                              </div>
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Footer */}
              {dirty && (
                <div className="px-5 py-3 border-t border-slate-100 bg-amber-50 flex items-center justify-between">
                  <p className="text-xs text-amber-700 flex items-center gap-1.5">
                    <AlertCircle size={13}/> You have unsaved changes
                  </p>
                  <button onClick={handleSave} disabled={saving}
                    className="px-4 py-1.5 bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold rounded-lg transition-colors disabled:opacity-60">
                    {saving ? 'Saving…' : 'Save Now'}
                  </button>
                </div>
              )}
            </div>
            );
            // Portal in fullscreen: ancestors with CSS transforms (animate-fade-in) would
            // otherwise become the containing block for `fixed`, offsetting the overlay.
            return isFullscreen ? createPortal(entryCard, document.body) : entryCard;
          })()}
        </>
      )}

      {/* ═══ DASHBOARD VIEW ═══ */}
      {isDashboardVisible && (
        <>
          {/* ═══ CLASS OVERVIEW — class-scoped users (teachers) ═══ */}
          {!hasSchoolView && (() => {
            const distinctClasses = [...new Map((myClasses || []).map(c => [c.classId, c])).values()];
            if (myClasses === null) return (
              <div className="glass-card p-10 flex items-center justify-center">
                <Loader2 size={24} className="animate-spin text-primary-500"/>
              </div>
            );
            if (!distinctClasses.length) return (
              <div className="glass-card p-10 text-center text-slate-400 text-sm">
                No classes assigned. Ask your admin to assign your class and subjects in User Access Control.
              </div>
            );
            const cd = classDash;
            const CPAL = ['#6ee7b7','#93c5fd','#d8b4fe','#fcd34d','#fca5a5','#a5b4fc','#99f6e4','#fbcfe8'];
            return (
              <div className="space-y-5">
                {/* Class selector pills */}
                <div className="glass-card p-4 flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">My Class</span>
                  {distinctClasses.map(c => (
                    <button key={c.classId} onClick={() => setOverviewClassId(c.classId)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                        overviewClassId === c.classId
                          ? 'bg-primary-50 text-primary-700 border-primary-300'
                          : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'}`}>
                      {c.grade}{c.section ? ` – ${c.section}` : ''}
                    </button>
                  ))}
                </div>

                {loadingClassDash ? (
                  <div className="glass-card p-10 flex items-center justify-center">
                    <Loader2 size={24} className="animate-spin text-primary-500"/>
                  </div>
                ) : !cd || !cd.students.length ? (
                  <div className="glass-card p-10 text-center text-slate-400 text-sm">
                    No tracker entries yet for this class. Grades entered in the Entry Form will appear here.
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                      {/* Subject-wise performance */}
                      <div className="glass-card p-5">
                        <h3 className="text-sm font-bold text-slate-700 mb-4">Subject-wise Performance — all students</h3>
                        <ResponsiveContainer width="100%" height={240}>
                          <BarChart data={cd.subjectWise} margin={{top:20,right:20,left:0,bottom:5}}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                            <XAxis dataKey="subject" tick={{fontSize:11,fill:'#64748b'}}/>
                            <YAxis domain={[0,100]} tickFormatter={v=>`${v}%`} tick={{fontSize:11,fill:'#64748b'}}/>
                            <Tooltip content={<ChartTip/>}/>
                            <Bar dataKey="score_pct" name="Score %" radius={[4,4,0,0]} maxBarSize={44}>
                              {cd.subjectWise.map((s,i)=><Cell key={i} fill={CPAL[i % CPAL.length]}/>)}
                              <LabelList dataKey="score_pct" position="top" formatter={v=>`${v}%`} style={{fontSize:10,fill:'#475569'}}/>
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                      {/* Week-wise progress */}
                      <div className="glass-card p-5">
                        <h3 className="text-sm font-bold text-slate-700 mb-4">Week-wise Progress</h3>
                        <ResponsiveContainer width="100%" height={240}>
                          <LineChart data={cd.weekWise} margin={{top:5,right:20,left:0,bottom:5}}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                            <XAxis dataKey="week" tick={{fontSize:11,fill:'#64748b'}}/>
                            <YAxis domain={[0,100]} tickFormatter={v=>`${v}%`} tick={{fontSize:11,fill:'#64748b'}}/>
                            <Tooltip content={<ChartTip/>}/>
                            <Legend iconType="circle" iconSize={8} wrapperStyle={{fontSize:11}}/>
                            <Line type="monotone" dataKey="overall" name="Overall" stroke="#475569" strokeWidth={2.5} dot={{r:3}}/>
                            {cd.subjects.map((sn,i)=>(
                              <Line key={sn} type="monotone" dataKey={sn} name={sn} stroke={CPAL[i % CPAL.length]} strokeWidth={2} dot={{r:2}}/>
                            ))}
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </div>

                    {/* Student × subject table */}
                    <div className="glass-card overflow-hidden">
                      <div className="px-5 py-4 border-b border-slate-100">
                        <h3 className="text-sm font-bold text-slate-700">
                          Student Performance — {cd.class.grade}{cd.class.section ? ` ${cd.class.section}` : ''}
                          <span className="text-slate-400 font-normal"> (average across all weeks)</span>
                        </h3>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="bg-slate-50 text-slate-500 uppercase tracking-wide">
                              <th className="px-4 py-2.5 text-left font-medium">Student</th>
                              {cd.subjects.map(sn => <th key={sn} className="px-3 py-2.5 text-center font-medium">{sn}</th>)}
                              <th className="px-3 py-2.5 text-center font-medium">Overall</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-50">
                            {cd.students.map(s => (
                              <tr key={s.id} className="hover:bg-slate-50/60">
                                <td className="px-4 py-2.5 font-medium text-slate-700">{s.name}</td>
                                {cd.subjects.map(sn => {
                                  const sub = s.subjects[sn];
                                  const cfg = sub?.grade ? GRADE_CFG[sub.grade] : null;
                                  return (
                                    <td key={sn} className="px-3 py-2.5 text-center">
                                      {cfg ? (
                                        <span title={`avg ${sub.avg} / 5`}
                                          className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-[11px] font-bold border ${cfg.bg} ${cfg.text} ${cfg.border}`}>
                                          {sub.grade}
                                        </span>
                                      ) : <span className="text-slate-300">—</span>}
                                    </td>
                                  );
                                })}
                                <td className="px-3 py-2.5 text-center">
                                  {s.overall_grade ? (
                                    <span title={`avg ${s.overall_score} / 5`}
                                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold border ${GRADE_CFG[s.overall_grade].bg} ${GRADE_CFG[s.overall_grade].text} ${GRADE_CFG[s.overall_grade].border}`}>
                                      {s.overall_grade}
                                      <span className="opacity-70 font-normal">– {GRADE_CFG[s.overall_grade].label}</span>
                                    </span>
                                  ) : <span className="text-slate-300">—</span>}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </>
                )}
              </div>
            );
          })()}

          {/* ═══ 16 School-Wide Analytics Charts (management / school-wide roles only) ═══ */}
          {hasSchoolView && !isTeacherScope && (() => {
            const cd = chartsData;
            if (loadingCharts && !cd) return (
              <div className="glass-card p-8 flex items-center justify-center gap-3">
                <Loader2 size={20} className="animate-spin text-primary-500"/>
                <span className="text-sm text-slate-400">Loading analytics…</span>
              </div>
            );
            if (!cd && chartsError) return (
              <div className="glass-card p-6 flex items-center gap-3 text-red-500 text-sm">
                <AlertCircle size={16} className="flex-shrink-0"/>
                <span>Analytics error: {chartsError}</span>
              </div>
            );
            if (!cd) return null;

            // ── Helpers ──────────────────────────────────────────
            const GCOL = { E:'#6ee7b7', P:'#93c5fd', M:'#d8b4fe', D:'#fcd34d', W:'#fca5a5' };
            const PALETTE = ['#6ee7b7','#6ee7b7','#93c5fd','#d8b4fe','#fca5a5','#ddd6fe','#99f6e4','#fcd34d'];
            const noData = (msg='No data yet') => (
              <div className="flex flex-col items-center justify-center h-32 text-slate-300 gap-2">
                <BarChart3 size={28}/>
                <span className="text-xs">{msg}</span>
              </div>
            );
            const CC = ({ title, subtitle, children, full=false }) => (
              <div className={`glass-card p-4 ${full ? 'col-span-2' : ''}`}>
                <div className="mb-3">
                  <h3 className="text-sm font-bold text-slate-700">{title}</h3>
                  {subtitle && <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>}
                </div>
                {children}
              </div>
            );
            const CT = ({ fmt='%' } = {}) => ({ active, payload, label }) => active && payload?.length ? (
              <div className="bg-white border border-slate-200 rounded-lg px-3 py-2 shadow-lg text-xs">
                <p className="font-semibold text-slate-700 mb-1">{label}</p>
                {payload.map((p,i) => (
                  <p key={i} style={{color: p.fill || p.color || p.stroke}}>{p.name}: <strong>{p.value}{fmt}</strong></p>
                ))}
              </div>
            ) : null;

            // ── Derived data ─────────────────────────────────────
            const gradeArr = ['E','P','M','D','W'].map(g => ({
              name: g, label: GRADE_CFG[g].label,
              value: cd.gradeDistribution.find(r=>r.grade===g)?.cnt || 0,
              fill: GCOL[g],
            })).filter(r => r.value > 0);

            const totalEntries = gradeArr.reduce((s,r)=>s+r.value,0);

            // Intervention status buckets from student rankings
            const srBuckets = { 'Excelling':0, 'Proficient':0, 'On Track':0, 'At Risk':0, 'Intervention':0 };
            const srColors  = { 'Excelling':'#6ee7b7','Proficient':'#93c5fd','On Track':'#d8b4fe','At Risk':'#fcd34d','Intervention':'#fca5a5' };
            cd.studentRankings.forEach(s => {
              if (s.avg_score >= 4.5)      srBuckets['Excelling']++;
              else if (s.avg_score >= 3.5) srBuckets['Proficient']++;
              else if (s.avg_score >= 2.5) srBuckets['On Track']++;
              else if (s.avg_score >= 1.5) srBuckets['At Risk']++;
              else                          srBuckets['Intervention']++;
            });
            const interventionPie = Object.entries(srBuckets)
              .filter(([,v])=>v>0)
              .map(([k,v])=>({ name:k, value:v, fill:srColors[k] }));

            // Grade-wise (aggregate by grade_level, e.g. I, II, III...)
            const gradeMap = {};
            cd.classAchievement.forEach(c => {
              if (!gradeMap[c.grade_level]) gradeMap[c.grade_level] = { grade_level: c.grade_level, scores:[], students:0 };
              gradeMap[c.grade_level].scores.push(parseFloat(c.score_pct));
              gradeMap[c.grade_level].students += c.students;
            });
            const gradeWise = Object.values(gradeMap).map(g => ({
              name: g.grade_level,
              score_pct: Math.round(g.scores.reduce((a,b)=>a+b,0)/g.scores.length * 10) / 10,
              students: g.students,
            })).sort((a,b) => a.name.localeCompare(b.name, undefined, { numeric: true }));

            // SRI — student risk index (risk score = 100 - score_pct, higher = more at risk)
            const sriData = cd.studentRankings.slice(0,20).map(s => ({
              name: s.name.length > 12 ? s.name.slice(0,11)+'…' : s.name,
              risk: Math.round(100 - s.score_pct),
              score: s.score_pct,
              class: s.class_label,
            }));

            // Weekly growth delta
            const growthData = cd.weeklyGrowth.map((w,i,arr) => ({
              name: w.week_name,
              score: w.score_pct,
              growth: i === 0 ? 0 : Math.round((w.score_pct - arr[i-1].score_pct)*10)/10,
            }));

            // Top/bottom 10 learning outcomes
            const topOutcomes    = cd.learningOutcomes.slice(0,10);
            const bottomOutcomes = [...cd.learningOutcomes].sort((a,b)=>a.mastery_pct-b.mastery_pct).slice(0,10);

            return (
              <div className="space-y-4">

                {/* ── Section header ── */}
                <div className="flex items-center gap-2 pt-1">
                  <BarChart3 size={15} className="text-primary-600"/>
                  <h3 className="text-sm font-bold text-slate-700">School Analytics</h3>
                  {loadingCharts && <Loader2 size={12} className="animate-spin text-slate-400"/>}
                </div>

                {/* Row 1 — Distribution + Intervention */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <CC title="School Performance Distribution" subtitle={`${totalEntries.toLocaleString()} total assessments`}>
                    {!gradeArr.length ? noData() : (
                      <ResponsiveContainer width="100%" height={240}>
                        <PieChart>
                          <Pie data={gradeArr} cx="50%" cy="50%" outerRadius={90} dataKey="value" label={({name,percent})=>`${name} ${(percent*100).toFixed(0)}%`} labelLine>
                            {gradeArr.map((g,i)=><Cell key={i} fill={g.fill}/>)}
                          </Pie>
                          <Tooltip formatter={(v,n)=>[`${v} assessments`, n]}/>
                          <Legend iconType="circle" iconSize={8} formatter={v=>`${v} – ${GRADE_CFG[v]?.label||v}`} wrapperStyle={{fontSize:11}}/>
                        </PieChart>
                      </ResponsiveContainer>
                    )}
                  </CC>

                  <CC title="Intervention Status" subtitle="Student risk level distribution">
                    {!interventionPie.length ? noData('Add tracker entries to see status') : (
                      <ResponsiveContainer width="100%" height={240}>
                        <PieChart>
                          <Pie data={interventionPie} cx="50%" cy="50%" innerRadius={55} outerRadius={90} dataKey="value" paddingAngle={3}
                            label={({name,value})=>`${name}: ${value}`} labelLine>
                            {interventionPie.map((p,i)=><Cell key={i} fill={p.fill}/>)}
                          </Pie>
                          <Tooltip formatter={(v,n)=>[`${v} students`,n]}/>
                          <Legend iconType="circle" iconSize={8} wrapperStyle={{fontSize:11}}/>
                        </PieChart>
                      </ResponsiveContainer>
                    )}
                  </CC>
                </div>

                {/* Row 2 — Grade-wise + Section-wise */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <CC title="Grade-wise Achievement" subtitle="Average performance % per school grade">
                    {!gradeWise.length ? noData() : (
                      <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={gradeWise} margin={{top:15,right:10,left:0,bottom:5}}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                          <XAxis dataKey="name" tick={{fontSize:11,fill:'#64748b'}}/>
                          <YAxis domain={[0,100]} tickFormatter={v=>`${v}%`} tick={{fontSize:10,fill:'#64748b'}}/>
                          <Tooltip content={CT()}/>
                          <ReferenceLine y={60} stroke="#93c5fd" strokeDasharray="4 4" label={{value:'Target 60%',fontSize:10,fill:'#93c5fd'}}/>
                          <Bar dataKey="score_pct" name="Avg Score" radius={[4,4,0,0]} maxBarSize={36}>
                            {gradeWise.map((g,i)=><Cell key={i} fill={g.score_pct>=80?'#93c5fd':g.score_pct>=60?'#6ee7b7':g.score_pct>=40?'#d8b4fe':'#fca5a5'}/>)}
                            <LabelList dataKey="score_pct" position="top" formatter={v=>`${v}%`} style={{fontSize:10,fill:'#475569'}}/>
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </CC>

                  <CC title="Section-wise Comparison" subtitle="Performance across all classes and sections">
                    {!cd.classAchievement.length ? noData() : (
                      <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={cd.classAchievement} margin={{top:15,right:10,left:0,bottom:30}}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                          <XAxis dataKey="class_label" tick={{fontSize:9,fill:'#64748b'}} angle={-35} textAnchor="end" interval={0}/>
                          <YAxis domain={[0,100]} tickFormatter={v=>`${v}%`} tick={{fontSize:10,fill:'#64748b'}}/>
                          <Tooltip content={CT()}/>
                          <ReferenceLine y={60} stroke="#93c5fd" strokeDasharray="4 4"/>
                          <Bar dataKey="score_pct" name="Score %" radius={[3,3,0,0]} maxBarSize={28}>
                            {cd.classAchievement.map((c,i)=><Cell key={i} fill={PALETTE[i%PALETTE.length]}/>)}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </CC>
                </div>

                {/* Row 3 — Subject-wise + Subject Risk */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <CC title="Subject-wise Achievement" subtitle="Average performance % per subject">
                    {!cd.subjectAchievement.length ? noData() : (
                      <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={cd.subjectAchievement} layout="vertical" margin={{top:5,right:40,left:10,bottom:5}}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                          <XAxis type="number" domain={[0,100]} tickFormatter={v=>`${v}%`} tick={{fontSize:10,fill:'#64748b'}}/>
                          <YAxis type="category" dataKey="subject" width={80} tick={{fontSize:11,fill:'#475569'}}/>
                          <Tooltip content={CT()}/>
                          <ReferenceLine x={60} stroke="#93c5fd" strokeDasharray="4 4"/>
                          <Bar dataKey="score_pct" name="Score %" radius={[0,4,4,0]} maxBarSize={22}>
                            {cd.subjectAchievement.map((s,i)=><Cell key={i} fill={s.score_pct>=80?'#93c5fd':s.score_pct>=60?'#6ee7b7':s.score_pct>=40?'#d8b4fe':'#fca5a5'}/>)}
                            <LabelList dataKey="score_pct" position="right" formatter={v=>`${v}%`} style={{fontSize:10,fill:'#475569'}}/>
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </CC>

                  <CC title="Subject Risk Analysis" subtitle="Grade distribution breakdown per subject">
                    {!cd.subjectAchievement.length ? noData() : (
                      <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={cd.subjectAchievement} margin={{top:5,right:10,left:0,bottom:5}}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                          <XAxis dataKey="subject" tick={{fontSize:11,fill:'#64748b'}}/>
                          <YAxis tick={{fontSize:10,fill:'#64748b'}} allowDecimals={false}/>
                          <Tooltip content={CT({fmt:''})}/>
                          <Legend iconType="circle" iconSize={8} wrapperStyle={{fontSize:10}}/>
                          {['E','P','M','D','W'].map(g=>(
                            <Bar key={g} dataKey={`${g.toLowerCase()}_cnt`} name={`${g}–${GRADE_CFG[g].label}`} stackId="a" fill={GCOL[g]} maxBarSize={50}/>
                          ))}
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </CC>
                </div>

                {/* Row 4 — Monthly Trend + Growth Index */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <CC title="Monthly Progress Trend" subtitle="School-wide avg performance over time">
                    {!cd.monthlyTrend.length ? noData('No monthly data yet') : (
                      <ResponsiveContainer width="100%" height={220}>
                        <AreaChart data={cd.monthlyTrend} margin={{top:10,right:20,left:0,bottom:5}}>
                          <defs>
                            <linearGradient id="gradTrend" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%"  stopColor="#6ee7b7" stopOpacity={0.5}/>
                              <stop offset="95%" stopColor="#6ee7b7" stopOpacity={0.05}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                          <XAxis dataKey="label" tick={{fontSize:10,fill:'#64748b'}}/>
                          <YAxis domain={[0,100]} tickFormatter={v=>`${v}%`} tick={{fontSize:10,fill:'#64748b'}}/>
                          <Tooltip content={CT()}/>
                          <ReferenceLine y={60} stroke="#93c5fd" strokeDasharray="4 4" label={{value:'Target',fontSize:9,fill:'#93c5fd'}}/>
                          <Area type="monotone" dataKey="score_pct" name="Avg Score" stroke="#6ee7b7" fill="url(#gradTrend)" strokeWidth={2} dot={{r:4,fill:'#6ee7b7'}}/>
                        </AreaChart>
                      </ResponsiveContainer>
                    )}
                  </CC>

                  <CC title="Growth Index" subtitle="Week-over-week score progression">
                    {!growthData.length ? noData('No weekly data yet') : (
                      <ResponsiveContainer width="100%" height={220}>
                        <ComposedChart data={growthData} margin={{top:10,right:20,left:0,bottom:5}}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                          <XAxis dataKey="name" tick={{fontSize:9,fill:'#64748b'}} angle={-20} textAnchor="end" interval={0}/>
                          <YAxis yAxisId="left" domain={[0,100]} tickFormatter={v=>`${v}%`} tick={{fontSize:10,fill:'#64748b'}}/>
                          <YAxis yAxisId="right" orientation="right" tickFormatter={v=>`${v>0?'+':''}${v}`} tick={{fontSize:10,fill:'#94a3b8'}}/>
                          <Tooltip content={CT()}/>
                          <ReferenceLine yAxisId="right" y={0} stroke="#94a3b8" strokeDasharray="3 3"/>
                          <Area yAxisId="left" type="monotone" dataKey="score" name="Score %" fill="#ede9fe" stroke="#6ee7b7" strokeWidth={2}/>
                          <Bar yAxisId="right" dataKey="growth" name="Δ Growth" maxBarSize={20} radius={[3,3,0,0]}>
                            {growthData.map((g,i)=><Cell key={i} fill={g.growth>=0?'#93c5fd':'#fca5a5'}/>)}
                          </Bar>
                        </ComposedChart>
                      </ResponsiveContainer>
                    )}
                  </CC>
                </div>

                {/* Row 5 — Class-wise + Attendance vs Achievement */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <CC title="Class-wise Achievement" subtitle="Average score % per class">
                    {!cd.classAchievement.length ? noData() : (
                      <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={cd.classAchievement} margin={{top:15,right:10,left:0,bottom:30}}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                          <XAxis dataKey="class_label" tick={{fontSize:9,fill:'#64748b'}} angle={-30} textAnchor="end" interval={0}/>
                          <YAxis domain={[0,100]} tickFormatter={v=>`${v}%`} tick={{fontSize:10,fill:'#64748b'}}/>
                          <Tooltip content={CT()}/>
                          <Bar dataKey="score_pct" name="Score %" radius={[4,4,0,0]} maxBarSize={32}>
                            {cd.classAchievement.map((c,i)=><Cell key={i} fill={c.score_pct>=80?'#93c5fd':c.score_pct>=60?'#6ee7b7':c.score_pct>=40?'#d8b4fe':'#fca5a5'}/>)}
                            <LabelList dataKey="score_pct" position="top" formatter={v=>`${v}%`} style={{fontSize:9,fill:'#475569'}}/>
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </CC>

                  <CC title="Attendance vs Achievement" subtitle="Monthly staff attendance vs academic performance">
                    {!cd.attendanceVsAchievement.length ? noData('No attendance data yet') : (
                      <ResponsiveContainer width="100%" height={220}>
                        <ComposedChart data={cd.attendanceVsAchievement} margin={{top:10,right:20,left:0,bottom:5}}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                          <XAxis dataKey="label" tick={{fontSize:9,fill:'#64748b'}}/>
                          <YAxis domain={[0,100]} tickFormatter={v=>`${v}%`} tick={{fontSize:10,fill:'#64748b'}}/>
                          <Tooltip content={CT()}/>
                          <Legend iconType="circle" iconSize={8} wrapperStyle={{fontSize:10}}/>
                          <Bar dataKey="academic_pct" name="Academic %" fill="#6ee7b7" opacity={0.8} radius={[3,3,0,0]} maxBarSize={28}/>
                          <Line type="monotone" dataKey="attendance_pct" name="Attendance %" stroke="#fcd34d" strokeWidth={2} dot={{r:4}} connectNulls/>
                        </ComposedChart>
                      </ResponsiveContainer>
                    )}
                  </CC>
                </div>

                {/* Row 6 — Learning Outcome Mastery (full width) */}
                <CC title="Learning Outcome Mastery" subtitle="% of students meeting or exceeding standard per sub-strand (sorted best to weakest)">
                  {!topOutcomes.length ? noData() : (
                    <ResponsiveContainer width="100%" height={Math.max(220, topOutcomes.length * 28)}>
                      <BarChart data={topOutcomes} layout="vertical" margin={{top:5,right:60,left:10,bottom:5}}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                        <XAxis type="number" domain={[0,100]} tickFormatter={v=>`${v}%`} tick={{fontSize:10,fill:'#64748b'}}/>
                        <YAxis type="category" dataKey="outcome" width={130} tick={{fontSize:10,fill:'#475569'}}/>
                        <Tooltip content={CT()}/>
                        <ReferenceLine x={60} stroke="#93c5fd" strokeDasharray="4 4" label={{value:'Target',fontSize:9,fill:'#93c5fd'}}/>
                        <Bar dataKey="mastery_pct" name="Mastery %" radius={[0,4,4,0]}>
                          {topOutcomes.map((o,i)=><Cell key={i} fill={o.mastery_pct>=80?'#93c5fd':o.mastery_pct>=60?'#6ee7b7':o.mastery_pct>=40?'#d8b4fe':'#fca5a5'}/>)}
                          <LabelList dataKey="mastery_pct" position="right" formatter={v=>`${v}%`} style={{fontSize:10,fill:'#475569'}}/>
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </CC>

                {/* Row 7 — Weakest Learning Outcomes (full width) */}
                <CC title="Weakest Learning Outcomes" subtitle="Sub-strands with lowest mastery — priority areas for intervention">
                  {!bottomOutcomes.length ? noData() : (
                    <ResponsiveContainer width="100%" height={Math.max(220, bottomOutcomes.length * 28)}>
                      <BarChart data={bottomOutcomes} layout="vertical" margin={{top:5,right:60,left:10,bottom:5}}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                        <XAxis type="number" domain={[0,100]} tickFormatter={v=>`${v}%`} tick={{fontSize:10,fill:'#64748b'}}/>
                        <YAxis type="category" dataKey="outcome" width={130} tick={{fontSize:10,fill:'#475569'}}/>
                        <Tooltip content={CT()}/>
                        <ReferenceLine x={60} stroke="#fca5a5" strokeDasharray="4 4" label={{value:'Target 60%',fontSize:9,fill:'#fca5a5'}}/>
                        <Bar dataKey="mastery_pct" name="Mastery %" radius={[0,4,4,0]}>
                          {bottomOutcomes.map((o,i)=><Cell key={i} fill={o.mastery_pct>=60?'#93c5fd':o.mastery_pct>=40?'#d8b4fe':'#fca5a5'}/>)}
                          <LabelList dataKey="mastery_pct" position="right" formatter={v=>`${v}%`} style={{fontSize:10,fill:'#475569'}}/>
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </CC>

                {/* Row 8 — Teacher Performance + Student Risk Index */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <CC title="Teacher Performance Index" subtitle="Average observation score per teacher (out of 5.0)">
                    {!cd.teacherPerformance.length ? noData('No observations recorded') : (
                      <div className="overflow-y-auto" style={{maxHeight:280}}>
                        <ResponsiveContainer width="100%" height={Math.max(220, cd.teacherPerformance.length * 28)}>
                          <BarChart data={cd.teacherPerformance} layout="vertical" margin={{top:5,right:50,left:10,bottom:5}}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                            <XAxis type="number" domain={[0,5]} tick={{fontSize:10,fill:'#64748b'}}/>
                            <YAxis type="category" dataKey="name" width={110} tick={{fontSize:10,fill:'#475569'}}/>
                            <Tooltip content={CT({fmt:''})} formatter={(v,n,p)=>[`${v}/5.00 (${p.payload.obs_cnt} obs)`,n]}/>
                            <ReferenceLine x={3} stroke="#d8b4fe" strokeDasharray="4 4" label={{value:'Min 3.0',fontSize:9,fill:'#d8b4fe'}}/>
                            <Bar dataKey="avg_score" name="Obs Score" radius={[0,4,4,0]}>
                              {cd.teacherPerformance.map((t,i)=><Cell key={i} fill={t.avg_score>=4?'#93c5fd':t.avg_score>=3?'#6ee7b7':t.avg_score>=2?'#d8b4fe':'#fca5a5'}/>)}
                              <LabelList dataKey="avg_score" position="right" formatter={v=>`${v}`} style={{fontSize:10,fill:'#475569'}}/>
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </CC>

                  <CC title="Student Risk Index (SRI)" subtitle="Risk score per student (higher = more at-risk)">
                    {!sriData.length ? noData() : (
                      <ResponsiveContainer width="100%" height={280}>
                        <BarChart data={sriData} layout="vertical" margin={{top:5,right:40,left:5,bottom:5}}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                          <XAxis type="number" domain={[0,100]} tickFormatter={v=>`${v}`} tick={{fontSize:10,fill:'#64748b'}}/>
                          <YAxis type="category" dataKey="name" width={90} tick={{fontSize:9,fill:'#475569'}}/>
                          <Tooltip content={CT({fmt:''})} formatter={(v,n,p)=>[`SRI: ${v} | Score: ${p.payload.score}%`,p.payload.class]}/>
                          <ReferenceLine x={40} stroke="#d8b4fe" strokeDasharray="4 4" label={{value:'Risk Threshold',fontSize:9,fill:'#d8b4fe'}}/>
                          <Bar dataKey="risk" name="Risk Index" radius={[0,3,3,0]}>
                            {sriData.map((s,i)=><Cell key={i} fill={s.risk>=80?'#fca5a5':s.risk>=60?'#fcd34d':s.risk>=40?'#d8b4fe':'#93c5fd'}/>)}
                            <LabelList dataKey="risk" position="right" style={{fontSize:9,fill:'#475569'}}/>
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </CC>
                </div>

                {/* Row 9 — Students Requiring Intervention + Bottom 20 */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <CC title="Students Requiring Intervention" subtitle="Students scoring Working (W) level — critical cases">
                    {(() => {
                      const wStudents = cd.studentRankings.filter(s => s.avg_score <= 2.0);
                      if (!wStudents.length) return (
                        <div className="flex flex-col items-center justify-center h-32 text-emerald-500 gap-2">
                          <CheckCircle2 size={28}/>
                          <span className="text-sm font-medium">No students require intervention</span>
                        </div>
                      );
                      return (
                        <div className="overflow-y-auto" style={{maxHeight:280}}>
                          <ResponsiveContainer width="100%" height={Math.max(180, wStudents.length * 28)}>
                            <BarChart data={wStudents} layout="vertical" margin={{top:5,right:50,left:10,bottom:5}}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                              <XAxis type="number" domain={[0,100]} tickFormatter={v=>`${v}%`} tick={{fontSize:10,fill:'#64748b'}}/>
                              <YAxis type="category" dataKey="name" width={100} tick={{fontSize:9,fill:'#475569'}}/>
                              <Tooltip content={CT()} formatter={(v,n,p)=>[`${v}% (${p.payload.class_label})`,n]}/>
                              <Bar dataKey="score_pct" name="Score %" fill="#fca5a5" radius={[0,4,4,0]}>
                                <LabelList dataKey="score_pct" position="right" formatter={v=>`${v}%`} style={{fontSize:9,fill:'#475569'}}/>
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      );
                    })()}
                  </CC>

                  <CC title="Bottom 20 Students" subtitle="Lowest performing students — ranked by avg score">
                    {!cd.studentRankings.length ? noData() : (
                      <div className="overflow-y-auto" style={{maxHeight:280}}>
                        <ResponsiveContainer width="100%" height={Math.max(220, Math.min(cd.studentRankings.length,20) * 28)}>
                          <BarChart data={cd.studentRankings.slice(0,20)} layout="vertical" margin={{top:5,right:50,left:10,bottom:5}}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                            <XAxis type="number" domain={[0,100]} tickFormatter={v=>`${v}%`} tick={{fontSize:10,fill:'#64748b'}}/>
                            <YAxis type="category" dataKey="name" width={100} tick={{fontSize:9,fill:'#475569'}}/>
                            <Tooltip content={CT()} formatter={(v,n,p)=>[`${v}% (${p.payload.class_label})`,n]}/>
                            <ReferenceLine x={60} stroke="#93c5fd" strokeDasharray="4 4"/>
                            <Bar dataKey="score_pct" name="Score %" radius={[0,4,4,0]}>
                              {cd.studentRankings.slice(0,20).map((s,i)=><Cell key={i} fill={s.score_pct>=60?'#6ee7b7':s.score_pct>=40?'#d8b4fe':'#fca5a5'}/>)}
                              <LabelList dataKey="score_pct" position="right" formatter={v=>`${v}%`} style={{fontSize:9,fill:'#475569'}}/>
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </CC>
                </div>

              </div>
            );
          })()}

          {/* ── Separator before subject-specific analytics ── */}
          <div className="flex items-center gap-3 pt-1">
            <div className="flex-1 border-t border-slate-200"/>
            <span className="text-[11px] text-slate-400 font-medium uppercase tracking-widest whitespace-nowrap">Subject Analytics</span>
            <div className="flex-1 border-t border-slate-200"/>
          </div>

          {!selSubject ? (
            <div className="glass-card p-12 text-center">
              <BarChart3 size={40} className="text-slate-300 mx-auto mb-3" />
              <p className="text-slate-400 font-medium">Select a subject to view analytics</p>
            </div>
          ) : loadingDash ? (
            <div className="glass-card p-12 flex items-center justify-center"><Loader2 size={28} className="animate-spin text-primary-500"/></div>
          ) : !dashData ? null : (
            <div className="space-y-5">

              {/* Grade distribution by strand */}
              {dashData.distribution?.length > 0 && (() => {
                // Build chart data: one row per strand with grade counts
                const strandMap = {};
                dashData.distribution.forEach(r => {
                  if (!strandMap[r.strand]) strandMap[r.strand] = { strand: r.strand };
                  strandMap[r.strand][r.grade] = r.cnt;
                });
                const chartData = Object.values(strandMap);
                return (
                  <div className="glass-card p-5">
                    <h3 className="text-sm font-bold text-slate-700 mb-4">Grade Distribution by Strand</h3>
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={chartData} margin={{top:20,right:20,left:0,bottom:5}}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                        <XAxis dataKey="strand" tick={{fontSize:11,fill:'#64748b'}}/>
                        <YAxis tick={{fontSize:11,fill:'#64748b'}} allowDecimals={false}/>
                        <Tooltip content={<ChartTip/>}/>
                        <Legend iconType="circle" iconSize={8} wrapperStyle={{fontSize:11}}/>
                        {GRADES.map(g=>(
                          <Bar key={g} dataKey={g} name={`${g} – ${GRADE_CFG[g].label}`}
                            fill={GRADE_CFG[g].color} radius={[3,3,0,0]} maxBarSize={28}>
                            <LabelList
                              dataKey={g}
                              position="top"
                              formatter={v => (v && v > 0) ? v : ''}
                              style={{ fontSize:10, fontWeight:600, fill: GRADE_CFG[g].color }}
                            />
                          </Bar>
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                );
              })()}

              {/* Student summary table */}
              {dashData.studentSummary?.length > 0 && (
                <div className="glass-card overflow-hidden">
                  <div className="px-5 py-4 border-b border-slate-100">
                    <h3 className="text-sm font-bold text-slate-700">Student Performance Summary
                      {selWeekObj && <span className="text-slate-400 font-normal"> — {selWeekObj.name}</span>}
                    </h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-slate-50 text-slate-500 uppercase tracking-wide">
                          <th className="px-4 py-2.5 text-left font-medium">Student</th>
                          {GRADES.map(g=>(
                            <th key={g} className="px-3 py-2.5 text-center font-medium">
                              <span className={`px-2 py-0.5 rounded-full ${GRADE_CFG[g].bg} ${GRADE_CFG[g].text} border ${GRADE_CFG[g].border}`}>{g}</span>
                            </th>
                          ))}
                          <th className="px-3 py-2.5 text-center font-medium">Total</th>
                          <th className="px-3 py-2.5 text-center font-medium">Overall</th>
                          <th className="px-4 py-2.5 text-left font-medium">Progress Bar</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-50">
                        {dashData.studentSummary.map(s => {
                          const counts = { W:s.w_count, D:s.d_count, M:s.m_count, P:s.p_count, E:s.e_count };
                          const total  = s.total || 1;
                          // Derived overall grade from backend avg_score (computed by backend)
                          const og  = s.overall_grade;
                          const ocfg = og ? GRADE_CFG[og] : null;
                          return (
                            <tr key={s.id} className="hover:bg-slate-50/60">
                              <td className="px-4 py-2.5 font-medium text-slate-700">{s.name}</td>
                              {GRADES.map(g=>(
                                <td key={g} className="px-3 py-2.5 text-center text-slate-700">{counts[g]||0}</td>
                              ))}
                              <td className="px-3 py-2.5 text-center text-slate-500">{s.total}</td>
                              <td className="px-3 py-2.5 text-center">
                                {ocfg ? (
                                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold border ${ocfg.bg} ${ocfg.text} ${ocfg.border}`}
                                    title={`${og} – ${ocfg.label} (avg score: ${Number(s.avg_score||0).toFixed(1)})`}>
                                    {og}
                                    <span className="opacity-70 font-normal">– {ocfg.label}</span>
                                  </span>
                                ) : <span className="text-slate-300">—</span>}
                              </td>
                              <td className="px-4 py-2.5 min-w-[140px]">
                                <div className="flex h-5 rounded-full overflow-hidden gap-px">
                                  {GRADES.map(g => counts[g] > 0 && (
                                    <div key={g}
                                      style={{ width:`${(counts[g]/total)*100}%`, background: GRADE_CFG[g].color }}
                                      title={`${g}: ${counts[g]}`}
                                      className="min-w-[4px]"
                                    />
                                  ))}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Student progress across weeks */}
              {dashData.studentProgress?.length > 0 && (() => {
                // Build a weekly trend for the class (aggregate)
                const weekMap = {};
                dashData.studentProgress.forEach(r => {
                  const key = r.week_name;
                  if (!weekMap[key]) weekMap[key] = { week: key };
                  weekMap[key][r.grade] = (weekMap[key][r.grade]||0) + r.count_per_grade;
                });
                const trendData = Object.values(weekMap);
                return (
                  <div className="glass-card p-5">
                    <h3 className="text-sm font-bold text-slate-700 mb-4">Weekly Progress Trend — Class Overview</h3>
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={trendData} margin={{top:5,right:20,left:0,bottom:5}}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                        <XAxis dataKey="week" tick={{fontSize:10,fill:'#64748b'}}/>
                        <YAxis tick={{fontSize:10,fill:'#64748b'}} allowDecimals={false}/>
                        <Tooltip content={<ChartTip/>}/>
                        <Legend iconType="circle" iconSize={8} wrapperStyle={{fontSize:10}}/>
                        {GRADES.map(g=>(
                          <Bar key={g} dataKey={g} name={`${g} – ${GRADE_CFG[g].label}`}
                            fill={GRADE_CFG[g].color} radius={[2,2,0,0]} maxBarSize={22} stackId="a"/>
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                );
              })()}

              {dashData.distribution?.length === 0 && dashData.studentSummary?.length === 0 && (
                <div className="glass-card p-12 text-center">
                  <CheckCircle2 size={40} className="text-slate-300 mx-auto mb-3"/>
                  <p className="text-slate-400">No data yet for selected filters. Start entering grades to see analytics.</p>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* ═══ SETUP VIEW (super admin only) ═══ */}
      {view === 'setup' && isSuperAdmin && (
        <div className="space-y-5">
          <TrackerSetup />
          <EmailNotificationSetup    module="learningTrackers" moduleLabel="Learning Trackers" yearId={yearId} />
          <TelegramNotificationSetup module="learningTrackers" moduleLabel="Learning Trackers" yearId={yearId} />
        </div>
      )}

    </div>
  );
}
